import React, { useState, useEffect, Fragment } from 'react';
import { useHistory, Link, useParams } from 'react-router-dom';
import Select from 'react-select';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, Button, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CopyrightIcon from '@mui/icons-material/Copyright';
// Assume fetchData and postData are defined elsewhere
import { fetchData, updateData, postData } from '../../../apiService';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import AsyncSelect from "react-select/async";  // ✅ instead of AsyncCreatableSelect
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
} from "@mui/material";

const InvoiceEdit = () => {
    const history = useHistory();
    const { id } = useParams(); // Use useParams to get the id from the URL  
    const { isAuthenticated, companyId, branchId, userId, logout, token } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchPaymentmethodTypes();
    }, [isAuthenticated]);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        fetchConsignmentType();
        fetchSource();
        fetchDestination();
        fetchExpense();
    }, []);

    useEffect(() => {
        fetchData('InvoiceApi', `/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data.Invoice);
                    setContainerItems(response.response_data.InvoiceContainerDetails);
                    setExpenseItems(response.response_data.InvoiceExpenseDetails);

                    setSelectedOption({
                        value: response.response_data.Invoice.CustomerId,
                        label: response.response_data.Invoice.CustomerName
                    });

                    setConsigneeSelectedOption({
                        value: response.response_data.Invoice.ConsigneeId,
                        label: response.response_data.Invoice.ConsigneeName
                    });

                    setSelectedConsignmentType({
                        value: response.response_data.Invoice.ConsignmentId,
                        label: response.response_data.Invoice.ConsignmentName
                    });

                    let ConsigneeId = response.response_data.Invoice.ConsigneeId;
                    let CustomerId = response.response_data.Invoice.CustomerId;
                    fetchAdvanceAmount(CustomerId, ConsigneeId);

                }
            })
            .catch((error) => {
                console.error('Error fetching Sale Details:', error);
            });
    }, [id]);

    const [formData, setFormData] = useState({
        CompanyId: companyId,
        ModifiedBy: userId,
        ModifiedDate: new Date().toISOString(),
    });

    const [containerItems, setContainerItems] = useState([]);
    const [expenseItems, setExpenseItems] = useState([]);
    const [advanceItems, setAdvanceItems] = useState([]);
    const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // FOR Customer
    const [CustomerOptions, setCustomerOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const fetchOptions = async (inputValue) => {
        if (inputValue.length <= 1) {
            return [];
        }

        try {
            const response = await fetchData('InvoiceApi', `/Customer/${companyId}/${inputValue}`, token);
            if (response.responseCode === '1111') {
                const data = await response.response_data;

                if (Array.isArray(data)) {
                    setCustomerOptions(data); // Save the options for later use
                    return data.map(item => ({
                        value: item.Id,
                        label: item.Name,
                    }));
                } else {
                    console.error('Data is not an array:', data);
                    return [];
                }
            } else {
                console.error('Unexpected response code:', response.responseCode);
                return [];
            }
        } catch (error) {
            console.error('Error fetching options:', error);
            return [];
        }
    };

    const handleCustomerSelect = (selectedOption) => {
        setSelectedOption(selectedOption);
        setFormData((prevData) => ({
            ...prevData,
            CustomerId: selectedOption ? selectedOption.value : '',
            CreatedBy: userId,
        }));

        if (!selectedOption) return;

        const selectedItemId = selectedOption.value;
        const selectedCustomerRetail = CustomerOptions.find((itemDetail) => itemDetail.Id === selectedItemId);
        const Vatno = selectedCustomerRetail?.VATNumber ?? '';
        const CustomerName = selectedCustomerRetail?.Name ?? '';

        let ConsigneeId = formData.ConsigneeId;
        let CustomerId = selectedOption.value;
        fetchAdvanceAmount(CustomerId, ConsigneeId);

        setFormData((prevData) => ({
            ...prevData,
            VATNumber: Vatno,
            CustomerName: CustomerName,
            CreatedBy: userId,
        }));
    };

    const handleInputChange = (newValue) => newValue;
    // Consignee
    const [ConsigneeOptions, setConsigneeOptions] = useState([]);
    const [ConsigneeselectedOption, setConsigneeSelectedOption] = useState(null);
    const ConsigneefetchOptions = async (inputValue) => {
        if (inputValue.length <= 1) {
            return [];
        }

        try {
            const response = await fetchData('InvoiceApi', `/Consignee/${companyId}/${inputValue}`, token);
            if (response.responseCode === '1111') {
                const data = await response.response_data;

                if (Array.isArray(data)) {
                    setConsigneeOptions(data); // Save the options for later use
                    return data.map(item => ({
                        value: item.Id,
                        label: item.Name,
                    }));
                } else {
                    console.error('Data is not an array:', data);
                    return [];
                }
            } else {
                console.error('Unexpected response code:', response.responseCode);
                return [];
            }
        } catch (error) {
            console.error('Error fetching options:', error);
            return [];
        }
    };

    const handleConsigneeSelect = (selectedOption) => {
        setConsigneeSelectedOption(selectedOption);
        setFormData((prevData) => ({
            ...prevData,
            ConsigneeId: selectedOption ? selectedOption.value : '',
            CreatedBy: userId,
        }));

        if (!selectedOption) return;

        const selectedItemId = selectedOption.value;
        const selectedConsignee = ConsigneeOptions.find((itemDetail) => itemDetail.Id === selectedItemId);
        const ConsigneeName = selectedConsignee?.Name ?? '';

        let ConsigneeId = selectedOption.value;
        let CustomerId = formData.CustomerId;
        fetchAdvanceAmount(CustomerId, ConsigneeId);

        setFormData((prevData) => ({
            ...prevData,
            ConsigneeName: ConsigneeName,
            CreatedBy: userId,
        }));
    };

    // FOR Advance Amount
    const fetchAdvanceAmount = async (CustomerId, ConsigneeId) => {

        // Validation: If CustomerId or ConsigneeId is null, undefined, or 0, return early
        if (!CustomerId || CustomerId === 0 || !ConsigneeId || ConsigneeId === 0) {
            return;
        }
        try {
            const response = await fetchData('InvoiceApi', `/AdvanceAmount/${CustomerId}/${ConsigneeId}`, token);
            if (response.responseCode === '1111') {
                const data = await response.response_data;

                setAdvanceItems(data);
                // ✅ Calculate total advance = sum of (AdvanceAmount - UsedAmount)
                const totalAdvanceAmount = data.reduce((sum, item) => {
                    const advance = Number(item.AdvanceAmount) || 0;
                    const used = Number(item.UsedAmount) || 0;
                    return sum + (advance - used);
                }, 0);

                // ✅ Round to 3 decimals
                const roundedTotal = Number(totalAdvanceAmount.toFixed(3));

                // ✅ Update form state with total
                setFormData(prevData => ({
                    ...prevData,
                    TotalAdvanceAmount: roundedTotal,
                }));

            } else {
                console.error('Unexpected response code:', response.responseCode);
                return [];
            }
        } catch (error) {
            console.error('Error fetching options:', error);
            return [];
        }
    };

    // For Consignment Type

    const handleCheckboxChange = (row) => {
        setAdvanceItems((prev) => {
            const updatedItems = prev.map((x) =>
                x.BillNumber === row.BillNumber
                    ? { ...x, isChecked: !x.isChecked }
                    : x
            );

            // ✅ Expense totals
            const Amount = expenseItems.reduce((sum, item) => sum + (parseFloat(item.Amount) || 0), 0);
            const VatAmount = expenseItems.reduce((sum, item) => sum + (parseFloat(item.VatAmount) || 0), 0);
            const TotalAmount = expenseItems.reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0);

            // ✅ Pass updatedItems to totalCalculation
            totalCalculation(Amount, VatAmount, TotalAmount, updatedItems);

            return updatedItems;
        });
    };

    const isRowChecked = (billNumber) => {
        const item = advanceItems.find((x) => x.BillNumber === billNumber);
        return item?.isChecked || false;
    };

    const [ConsignmentTypeOptions, setConsignmentTypeOptions] = useState([]);
    const [selectedConsignmentType, setSelectedConsignmentType] = useState("");
    const fetchConsignmentType = async () => {
        try {
            const res = await fetchData('InvoiceApi', `/Consignment/${companyId}`, token);
            if (res.responseCode === '1111') {
                const options = res.response_data.map(c => ({ value: c.Id, label: c.Name }));
                setConsignmentTypeOptions(options);
            }
        } catch (e) { console.error('Error fetching ConsignmentType:', e); }
    };

    const handleConsignmentTypeChange = async (opt) => {
        setSelectedConsignmentType(opt);
        setFormData(prev => ({ ...prev, CreatedBy: userId, CompanyId: companyId, ConsignmentId: opt.value }));
    };

    // Source
    const [SourceOptions, setSourceOptions] = useState([]);
    const [selectedSource, setSelectedSource] = useState("");
    const fetchSource = async () => {
        try {
            const res = await fetchData('InvoiceApi', `/Source/${companyId}`, token);
            if (res.responseCode === '1111') {
                const options = res.response_data.map(c => ({ value: c.Id, label: c.Name }));
                setSourceOptions(options);
            }
        } catch (e) { console.error('Error fetching Source:', e); }
    };

    const handleSourceChange = async (opt) => {
        setSelectedSource(opt);
        setModalDataContainer((prevData) => ({
            ...prevData,
            SourceId: opt ? opt.value : '',
            SourceName: opt ? opt.label : '',
            CreatedBy: userId,
        }));
    };

    // Destination
    const [DestinationOptions, setDestinationOptions] = useState([]);
    const [selectedDestination, setSelectedDestination] = useState("");
    const fetchDestination = async () => {
        try {
            const res = await fetchData('InvoiceApi', `/Destination/${companyId}`, token);
            if (res.responseCode === '1111') {
                const options = res.response_data.map(c => ({ value: c.Id, label: c.Name }));
                setDestinationOptions(options);
            }
        } catch (e) { console.error('Error fetching Destination:', e); }
    };

    const handleDestinationChange = async (opt) => {
        setSelectedDestination(opt);
        setModalDataContainer((prevData) => ({
            ...prevData,
            DestinationId: opt ? opt.value : '',
            DestinationName: opt ? opt.label : '',
            CreatedBy: userId,
        }));
    };

    // Expense
    const [ExpenseOptions, setExpenseOptions] = useState([]);
    const [selectedExpense, setSelectedExpense] = useState("");
    const fetchExpense = async () => {
        try {
            const res = await fetchData('InvoiceApi', `/IncomeExpense/${companyId}`, token);
            if (res.responseCode === '1111') {
                const options = res.response_data.map(c => ({ value: c.Id, label: c.Name }));
                setExpenseOptions(options);
            }
        } catch (e) { console.error('Error fetching Expense:', e); }
    };

    const handleExpenseChange = async (opt) => {
        setSelectedExpense(opt);
        setModalDataExpense((prevData) => ({
            ...prevData,
            IncomeExpenseId: opt ? opt.value : '',
            IncomeExpenseName: opt ? opt.label : '',
            CreatedBy: userId,
        }));
    };

    const formatDateForInput = (dateString) => {
        const d = new Date(dateString);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setIsLoading(true);

        if (!formData.InvoiceNumber) {
            setErrorMessage('Invoice No is required !');
            setIsLoading(false);
            return;
        }

        if (!formData.CustomerId || formData.CustomerId === 0) {
            setErrorMessage('Customer is required !');
            setIsLoading(false);
            return;
        }

        if (!formData.ConsigneeId || formData.ConsigneeId === 0) {
            setErrorMessage('Consignee is required !');
            setIsLoading(false);
            return;
        }

        if (!formData.ConsignmentId || formData.ConsignmentId === 0) {
            setErrorMessage('Consignment Type is required !');
            setIsLoading(false);
            return;
        }


        // // Validate at least one item is selected
        // if (!containerItems || containerItems.length === 0) {
        //     setErrorMessage('At least one Container Items must be selected to continue!');
        //     setIsLoading(false);
        //     return;
        // }

        if (!expenseItems || expenseItems.length === 0) {
            setErrorMessage('At least one Expense must be selected to continue!');
            setIsLoading(false);
            return;
        }

        setFormData(prevData => ({
            ...prevData,
            CreatedBy: userId,
            CompanyId: companyId,
        }));

        const InvoiceVM = {
            Invoice: formData,
            InvoiceContainerDetails: containerItems,
            InvoiceExpenseDetails: expenseItems,
            PaymentAndReceiptAdvanceDetails: advanceItems
        };

        // Validate form data here if needed
        updateData('InvoiceApi', `${id}`, InvoiceVM, token)
            .then((response) => {
                console.log('Record updated successfully:', response.data);
                const SaleId = response.kotId;
                generateInvoice(SaleId); // Handle print with SaleId
                history.push('/InvoiceIndex');
            })
            .catch((error) => {
                console.error('Error updating record:', error);
            });
    };

    // const generateInvoice = async (saleId) => {
    //     try {
    //         // ===== FETCH DATA =====
    //         const response = await fetchData('InvoiceApi', `/SalePrint/${saleId}`, token);
    //         const labelData = response.response_data;

    //         if (!labelData?.SalePrint || !labelData?.SalePrintItems) {
    //             throw new Error("Invalid invoice data.");
    //         }

    //         const header = labelData.SalePrint;
    //         const items = labelData.SalePrintItems;

    //         // ===== INITIALIZE PDF =====
    //         const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    //         let pageWidth = doc.internal.pageSize.getWidth();
    //         const boxX = 5;
    //         const boxWidth = pageWidth - 10;
    //         const rowHeight = 7;
    //         const midX = pageWidth - 65;

    //         // ===== TOP SECTION =====
    //         doc.setFontSize(18).setFont("times", "bold");

    //         // TAX INVOICE (centered)
    //         doc.text("TAX INVOICE", pageWidth / 2, 55, { align: "center" });

    //         // VAT Number (right aligned)
    //         doc.setFontSize(10).setFont("times", "bold");
    //         doc.text(`Our VATIN : ${header.CompanyVATNumber || ''}`, pageWidth - 7, 55, { align: "right" });

    //         // move y start below this
    //         let y = 61;

    //         // ===== HEADER SECTION =====
    //         doc.setLineWidth(0.2);
    //         doc.rect(boxX, y, boxWidth, rowHeight * 4);

    //         // Horizontal dividers
    //         for (let i = 1; i <= 3; i++) {
    //             doc.line(boxX, y + rowHeight * i, boxX + boxWidth, y + rowHeight * i);
    //         }

    //         // Vertical dividers
    //         doc.line(midX, y + rowHeight, midX, y + rowHeight * 2);
    //         doc.line(midX, y + rowHeight * 2, midX, y + rowHeight * 3);

    //         // Row 1: Customer
    //         doc.setFontSize(11).setFont("times", "normal");
    //         doc.text("Customer :", boxX + 2, y + 5);
    //         doc.setFont("times", "bold");
    //         doc.text(header.CustomerName || '', boxX + 28, y + 5);

    //         // Row 2: VAT Number + Invoice No
    //         doc.setFont("times", "normal");
    //         doc.text("VAT Number :", boxX + 2, y + rowHeight + 5);
    //         doc.setFont("times", "bold");
    //         doc.text(header.CustomerVatNumber || '', boxX + 28, y + rowHeight + 5);

    //         doc.setFont("times", "normal");
    //         doc.text("Invoice No :", midX + 2, y + rowHeight + 5);
    //         doc.setFont("times", "bold");
    //         doc.text(header.BillNumber || '', midX + 23, y + rowHeight + 5);

    //         // Row 3: Consignee + Dated
    //         doc.setFont("times", "normal");
    //         doc.text("Consignee :", boxX + 2, y + rowHeight * 2 + 5);
    //         doc.setFont("times", "bold");
    //         doc.text(header.ConsigneeName || '', boxX + 28, y + rowHeight * 2 + 5);

    //         doc.setFont("times", "normal");
    //         doc.text("Date :", midX + 2, y + rowHeight * 2 + 5);
    //         doc.setFont("times", "bold");
    //         doc.text(header.BillDate ? new Date(header.BillDate).toLocaleDateString('en-GB') : '', midX + 23, y + rowHeight * 2 + 5);

    //         // Row 4: BL Number
    //         doc.setFont("times", "normal");
    //         doc.text("BL Number :", boxX + 2, y + rowHeight * 3 + 5);
    //         doc.setFont("times", "bold");
    //         doc.text(header.BLNumber || '', boxX + 28, y + rowHeight * 3 + 5);

    //         y += rowHeight * 4;

    //         const taxableAmount = items.reduce((sum, i) => sum + (i.Amount ?? 0), 0);
    //         const totalAmount = items.reduce((sum, i) => sum + (i.TotalAmount ?? 0), 0);
    //         const totalVat = items.reduce((sum, i) => sum + (i.VatAmount ?? 0), 0);
    //         const advance = Number(header.AdvanceAmount || 0);
    //         const balance = (totalAmount - advance).toFixed(3);

    //         // ===== ITEMS TABLE =====
    //         const itemRows = items.map((item, idx) => {
    //             if (!item.ParticularsName) {
    //                 return ["", "", "", "", "", ""];
    //             }
    //             return [
    //                 (idx + 1).toString(),
    //                 `${item.ParticularsName}${item.Remarks ? ` ( ${item.Remarks} )` : ''}`,
    //                 (item.Amount ?? 0).toFixed(3),
    //                 (item.VatRate ?? 0).toFixed(3),
    //                 (item.VatAmount ?? 0).toFixed(3),
    //                 (item.TotalAmount ?? 0).toFixed(3)
    //             ];
    //         });

    //         const sumAmount = items.reduce((sum, i) => sum + (i.Amount ?? 0), 0);
    //         const sumVat = items.reduce((sum, i) => sum + (i.VatAmount ?? 0), 0);
    //         const sumTotal = items.reduce((sum, i) => sum + (i.TotalAmount ?? 0), 0);

    //         autoTable(doc, {
    //             startY: y,
    //             head: [['No', 'Particulars', 'Amount (RO)', 'VAT %', 'VAT Amount (RO)', 'Total Amount (RO)']],
    //             body: [
    //                 ...itemRows,
    //                 ['', 'TOTAL', sumAmount.toFixed(3), '', sumVat.toFixed(3), sumTotal.toFixed(3)]
    //             ],
    //             styles: {
    //                 font: "times",           // ✅ Use Times New Roman
    //                 fontSize: 10,            // ✅ Font size 10
    //                 lineColor: [0, 0, 0],
    //                 textColor: [0, 0, 0],
    //                 lineWidth: 0.2,
    //                 cellPadding: 2,
    //                 halign: 'center'
    //             },
    //             headStyles: {
    //                 fillColor: [240, 240, 240],
    //                 textColor: [0, 0, 0],
    //                 font: "times",          // ✅ Ensure header also uses Times New Roman
    //                 fontStyle: "bold",
    //                 fontSize: 8,           // ✅ Consistent font size
    //                 lineWidth: 0.2
    //             },
    //             bodyStyles: {
    //                 font: "times",
    //                 fontSize: 9,
    //                 halign: 'right'
    //             },
    //             columnStyles: {
    //                 0: { cellWidth: 8, halign: 'center' },
    //                 1: { cellWidth: 102, halign: 'left' },
    //                 2: { cellWidth: 21, halign: 'right' },
    //                 3: { cellWidth: 14, halign: 'right' },
    //                 4: { cellWidth: 27, halign: 'right' },
    //                 5: { cellWidth: 28, halign: 'right' }
    //             },
    //             didParseCell: (data) => {
    //                 if (data.row.index === itemRows.length) {
    //                     // ✅ Highlight TOTAL row
    //                     data.cell.styles.font = "times";
    //                     data.cell.styles.fontSize = 10;
    //                     data.cell.styles.fontStyle = 'bold';
    //                     data.cell.styles.fillColor = [240, 240, 240];
    //                 }
    //             },
    //             theme: "grid",
    //             margin: { left: 5, right: 5 }
    //         });

    //         // ===== TOTALS SECTION =====
    //         pageWidth = doc.internal.pageSize.getWidth();
    //         y = doc.lastAutoTable.finalY;
    //         const totalRowHeight = 8;

    //         doc.setLineWidth(0.1);



    //         // === Define layout ===
    //         const boxXx = 5;
    //         const boxWidthx = pageWidth - 10;
    //         const boxHeight = 7;

    //         // Keep left layout same
    //         const colSplitLeft = boxXx + boxWidthx * 0.655; // separation between left and right
    //         // Add extra column split on right side for label/value separation
    //         const colSplitRight = boxXx + boxWidthx * 0.86;

    //         // ===== Row 1: Total VAT + Advance =====
    //         doc.setLineWidth(0.2);
    //         doc.rect(boxXx, y, boxWidthx, boxHeight);

    //         // Draw column separators
    //         doc.line(colSplitLeft, y, colSplitLeft, y + boxHeight);
    //         doc.line(colSplitRight, y, colSplitRight, y + boxHeight);

    //         doc.setFont("times", "bold").setFontSize(10);

    //         // Left side (same as before)
    //         doc.text(`Total VAT Amount :  ${totalVat.toFixed(3)}`, boxXx + 2, y + 5);

    //         // Right side (split into label/value)
    //         doc.text("Advance Amount ", colSplitLeft + 3, y + 5);
    //         doc.text(`${advance.toFixed(3)}`, boxXx + boxWidthx - 3, y + 5, { align: "right" });
    //         y += boxHeight;

    //         // ===== Row 2: Invoice without VAT + Balance =====
    //         doc.rect(boxXx, y, boxWidthx, boxHeight);
    //         doc.line(colSplitLeft, y, colSplitLeft, y + boxHeight);
    //         doc.line(colSplitRight, y, colSplitRight, y + boxHeight);

    //         // Left side (same as before)
    //         doc.text(`Invoice Amount without VAT :  ${taxableAmount.toFixed(3)}`, boxXx + 2, y + 5);

    //         // Right side (split into label/value)
    //         doc.text("Balance Amount ", colSplitLeft + 3, y + 5);
    //         doc.text(`${balance}`, boxXx + boxWidthx - 3, y + 5, { align: "right" });
    //         y += boxHeight;



    //         // === Amount in Words + Remarks + Declaration Section ===
    //         const sectionTop = y;
    //         const boxXa = 5;
    //         const boxWidtha = pageWidth - 10;
    //         let ty = sectionTop + 7;

    //         // === 1️⃣ Amount in Words ===
    //         doc.setFont("times", "normal").setFontSize(10);
    //         const labelText = "Amount Chargeable in words:";
    //         doc.text(labelText, boxXa + 2, ty);

    //         doc.setFont("times", "bold");
    //         const amountInWords = ConvertAmountToRialBaisa(balance);
    //         const amountX = boxXa + 2 + doc.getTextWidth(labelText) + 3;
    //         doc.text(` ${amountInWords}`, amountX, ty);

    //         ty += 8;

    //         // === 2️⃣ Remarks ===
    //         doc.setFont("times", "bold").setFontSize(10);
    //         doc.text("Remarks", boxXa + 2, ty);
    //         ty += 6;

    //         doc.setFont("times", "normal").setFontSize(10);
    //         const remarksText = `${header.Remarks1 || ""} ${header.Remarks2 || ""}`.trim();
    //         if (remarksText) {
    //             const remarksLines = doc.splitTextToSize(remarksText, boxWidtha - 10);
    //             doc.text(remarksLines, boxXa + 4, ty);
    //             ty += remarksLines.length * 4;
    //         } else {
    //             ty += 4;
    //         }

    //         // === 3️⃣ Declaration ===
    //         doc.setFont("times", "bold").setFontSize(10);
    //         doc.text("Declaration", boxXa + 2, ty + 3);
    //         ty += 8;

    //         doc.setFont("times", "normal").setFontSize(10);
    //         const declarationLines = [
    //             "1. Insurance of cargo by client.",
    //             "2. All commercial documents attached with invoice to be kept on record for 5 years from the date of custom clearance, to be",
    //             "   submitted to DG Customs on demand."
    //         ];
    //         doc.text(declarationLines, boxXa + 4, ty);

    //         // calculate total height based on content
    //         const lineHeight = 4;
    //         const bottomPadding = 4;
    //         ty += declarationLines.length * lineHeight + bottomPadding;

    //         // Draw the box dynamically
    //         const sectionHeight = ty - sectionTop;
    //         doc.rect(boxXa, sectionTop, boxWidtha, sectionHeight);

    //         // ===== SIGNATURE SECTION =====
    //         y = sectionTop + sectionHeight;

    //         const margin = 5;
    //         const leftWidth = 70; // Left box width
    //         const rightWidth = pageWidth - 2 * margin - leftWidth;
    //         const topHeight = 12;
    //         const bottomHeight = 12;

    //         // === LEFT BOX ===
    //         doc.rect(margin, y, leftWidth, topHeight + bottomHeight);
    //         doc.setFont("times", "bold").setFontSize(10);
    //         doc.text("Customers Seal and Signature", margin + 5, y + topHeight + 5);

    //         // === RIGHT BOX ===
    //         const rightX = margin + leftWidth;
    //         doc.rect(rightX, y, rightWidth, topHeight); // top right

    //         // Set font and place text inside the box (left aligned with small padding)
    //         doc.setFont("times", "bold").setFontSize(10);
    //         doc.text("For Rashid Al Hadi Trading", rightX + 5, y + 7); // ✅ left-aligned inside column

    //         // === Bottom right 3-column section ===
    //         const bottomY = y + topHeight;
    //         doc.rect(rightX, bottomY, rightWidth, bottomHeight);

    //         const colW = rightWidth / 3;


    //         doc.setFont("times", "bold").setFontSize(9);
    //         // Move text slightly lower (was +7 → now +9)
    //         const labelYOffset = 10;

    //         doc.text("Prepared by", rightX + colW / 2, bottomY + labelYOffset, { align: "center" });
    //         doc.text("Verified By", rightX + colW * 1.5, bottomY + labelYOffset, { align: "center" });
    //         doc.text("Authorized Signatory", rightX + colW * 2.5, bottomY + labelYOffset, { align: "center" });

    //         // // ===== OPEN & PRINT =====
    //         const blob = doc.output('blob');
    //         const blobUrl = URL.createObjectURL(blob);
    //         const printWindow = window.open(blobUrl, '_blank');
    //         if (printWindow) {
    //             printWindow.onload = () => {
    //                 printWindow.focus();
    //                 printWindow.print();
    //             };
    //         }

    //     } catch (err) {
    //         console.error("Error generating invoice:", err);
    //     }
    // };

    const generateInvoice = async (saleId) => {
        try {
            // ===== FETCH DATA =====
            const response = await fetchData('InvoiceApi', `/SalePrint/${saleId}`, token);
            const labelData = response.response_data;

            if (!labelData?.SalePrint || !labelData?.SalePrintItems) {
                throw new Error("Invalid invoice data.");
            }

            const header = labelData.SalePrint;
            const items = labelData.SalePrintItems;

            // ===== INITIALIZE PDF =====
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            let pageWidth = doc.internal.pageSize.getWidth();
            const boxX = 5;
            const boxWidth = pageWidth - 10;
            const rowHeight = 7;
            const midX = pageWidth - 65;

            // ===== TOP SECTION =====
            doc.setFontSize(18).setFont("times", "bold");

            // TAX INVOICE (centered)
            doc.text("TAX INVOICE", pageWidth / 2, 50, { align: "center" });

            // VAT Number (right aligned)
            doc.setFontSize(11).setFont("times", "bold");
            doc.text(`Our VATIN : ${header.CompanyVATNumber || ''}`, pageWidth - 7, 50, { align: "right" });

            // move y start below this
            let y = 56;

            // ===== HEADER SECTION =====
            const labelX = boxX + 2;
            const valueX = boxX + 28;
            const invoiceLabelX = midX + 2;
            const invoiceValueX = midX + 23;

            const baseRowHeight = 8; // compact row height
            const consigneeLineHeight = 5; // tighter line height

            // Split consignee text into lines
            const consigneeMaxWidth = midX - valueX - 5;
            const consigneeName = header.ConsigneeName || '';
            const consigneeLines = doc.splitTextToSize(consigneeName, consigneeMaxWidth);
            const consigneeHeight = consigneeLines.length * consigneeLineHeight;

            // Dynamic row height for consignee/date row
            const row1Height = baseRowHeight; // Customer
            const row2Height = baseRowHeight; // VAT / Invoice
            const row3Height = Math.max(consigneeHeight + 2, baseRowHeight); // Consignee / Date
            const row4Height = baseRowHeight; // BL Number
            const totalBoxHeight = row1Height + row2Height + row3Height + row4Height;

            // ===== Draw Outer Border =====
            doc.setLineWidth(0.2);
            doc.rect(boxX, y, boxWidth, totalBoxHeight);

            // ===== Horizontal Lines =====
            let currentY = y;
            doc.line(boxX, currentY + row1Height, boxX + boxWidth, currentY + row1Height);               // after Customer
            doc.line(boxX, currentY + row1Height + row2Height, boxX + boxWidth, currentY + row1Height + row2Height); // after VAT/Invoice
            doc.line(boxX, currentY + row1Height + row2Height + row3Height, boxX + boxWidth, currentY + row1Height + row2Height + row3Height); // after Consignee/Date

            // ===== Vertical Divider =====
            doc.line(midX, y + row1Height, midX, y + row1Height + row2Height + row3Height); // full height across VAT+Consignee sections

            // ===== Row 1: Customer =====
            doc.setFontSize(11).setFont("times", "normal");
            doc.text("Customer :", boxX + 2, y + 5);
            doc.setFont("times", "bold");
            doc.text(header.CustomerName || '', boxX + 28, y + 5);

            // ===== Row 2: VAT / Invoice =====
            doc.setFontSize(11).setFont("times", "normal");
            doc.text("VAT Number :", boxX + 2, y + row1Height + 5);
            doc.setFont("times", "bold");
            doc.text(header.CustomerVatNumber || '', boxX + 28, y + row1Height + 5);

            doc.setFontSize(11).setFont("times", "normal");
            doc.text("Invoice No :", invoiceLabelX, y + row1Height + 5);
            doc.setFont("times", "bold");
            doc.text(header.BillNumber || '', invoiceValueX, y + row1Height + 5);

            // ===== Row 3: Consignee / Date =====
            const consigneeY = y + row1Height + row2Height + 5;
            doc.setFontSize(11).setFont("times", "normal");
            doc.text("Consignee :", labelX, consigneeY);
            doc.setFont("times", "bold");
            doc.text(consigneeLines, valueX, consigneeY);

            doc.setFontSize(11).setFont("times", "normal");
            doc.text("Date :", invoiceLabelX, consigneeY);
            doc.setFont("times", "bold");
            doc.text(
                header.BillDate ? new Date(header.BillDate).toLocaleDateString('en-GB') : '',
                invoiceValueX,
                consigneeY
            );

            // ===== Row 4: BL Number =====
            const blY = y + row1Height + row2Height + row3Height + 5;
            doc.setFontSize(11).setFont("times", "normal");
            doc.text("BL Number :", boxX + 2, blY);
            doc.setFont("times", "bold");
            doc.text(header.BLNumber || '', boxX + 28, blY);

            // ===== Update Y (Start of Table Section) =====
            y = y + totalBoxHeight;


            const taxableAmount = items.reduce((sum, i) => sum + (i.Amount ?? 0), 0);
            const totalAmount = items.reduce((sum, i) => sum + (i.TotalAmount ?? 0), 0);
            const totalVat = items.reduce((sum, i) => sum + (i.VatAmount ?? 0), 0);
            const advance = Number(header.AdvanceAmount || 0);
            const balance = (totalAmount - advance).toFixed(3);

            // ===== ITEMS TABLE =====
            const itemRows = items.map((item, idx) => {
                if (!item.ParticularsName) {
                    return ["", "", "", "", "", ""];
                }
                return [
                    (idx + 1).toString(),
                    `${item.ParticularsName}${item.Remarks ? ` ( ${item.Remarks} )` : ''}`,
                    (item.Amount ?? 0).toFixed(3),
                    (item.VatRate ?? 0).toFixed(3),
                    (item.VatAmount ?? 0).toFixed(3),
                    (item.TotalAmount ?? 0).toFixed(3)
                ];
            });

            const sumAmount = items.reduce((sum, i) => sum + (i.Amount ?? 0), 0);
            const sumVat = items.reduce((sum, i) => sum + (i.VatAmount ?? 0), 0);
            const sumTotal = items.reduce((sum, i) => sum + (i.TotalAmount ?? 0), 0);

            autoTable(doc, {
                startY: y,
                head: [['No', 'Particulars', 'Amount (RO)', 'VAT %', 'VAT Amount (RO)', 'Total Amount (RO)']],
                body: [
                    ...itemRows,
                    ['', 'TOTAL', sumAmount.toFixed(3), '', sumVat.toFixed(3), sumTotal.toFixed(3)]
                ],
                styles: {
                    font: "times",           // ✅ Use Times New Roman
                    fontSize: 10,            // ✅ Font size 10
                    lineColor: [0, 0, 0],
                    textColor: [0, 0, 0],
                    lineWidth: 0.2,
                    cellPadding: 2,
                    halign: 'center'
                },
                headStyles: {
                    fillColor: [240, 240, 240],
                    textColor: [0, 0, 0],
                    font: "times",          // ✅ Ensure header also uses Times New Roman
                    fontStyle: "bold",
                    fontSize: 8,           // ✅ Consistent font size
                    lineWidth: 0.2
                },
                bodyStyles: {
                    font: "times",
                    fontSize: 9,
                    halign: 'right'
                },
                columnStyles: {
                    0: { cellWidth: 8, halign: 'center' },
                    1: { cellWidth: 102, halign: 'left' },
                    2: { cellWidth: 21, halign: 'right' },
                    3: { cellWidth: 14, halign: 'right' },
                    4: { cellWidth: 27, halign: 'right' },
                    5: { cellWidth: 28, halign: 'right' }
                },
                didParseCell: (data) => {
                    if (data.row.index === itemRows.length) {
                        // ✅ Highlight TOTAL row
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = [240, 240, 240];
                        data.cell.styles.fontSize = 10;
                    }
                },
                theme: "grid",
                margin: { left: 5, right: 5 }
            });

            // ===== TOTALS SECTION =====
            pageWidth = doc.internal.pageSize.getWidth();
            y = doc.lastAutoTable.finalY;
            const totalRowHeight = 8;

            doc.setLineWidth(0.1);



            // === Define layout ===
            const boxXx = 5;
            const boxWidthx = pageWidth - 10;
            const boxHeight = 7;

            // Keep left layout same
            const colSplitLeft = boxXx + boxWidthx * 0.655; // separation between left and right
            // Add extra column split on right side for label/value separation
            const colSplitRight = boxXx + boxWidthx * 0.86;

            // ===== Row 1: Total VAT + Advance =====
            doc.setLineWidth(0.2);
            doc.rect(boxXx, y, boxWidthx, boxHeight);

            // Draw column separators
            doc.line(colSplitLeft, y, colSplitLeft, y + boxHeight);
            doc.line(colSplitRight, y, colSplitRight, y + boxHeight);

            doc.setFont("times", "bold").setFontSize(10);

            // Left side (same as before)
            doc.text(`Total VAT Amount :  ${totalVat.toFixed(3)}`, boxXx + 2, y + 5);

            // Right side (split into label/value)
            doc.text("Advance Amount ", colSplitLeft + 3, y + 5);
            doc.text(`${advance.toFixed(3)}`, boxXx + boxWidthx - 3, y + 5, { align: "right" });
            y += boxHeight;

            // ===== Row 2: Invoice without VAT + Balance =====
            doc.rect(boxXx, y, boxWidthx, boxHeight);
            doc.line(colSplitLeft, y, colSplitLeft, y + boxHeight);
            doc.line(colSplitRight, y, colSplitRight, y + boxHeight);

            // Left side (same as before)
            doc.text(`Invoice Amount without VAT :  ${taxableAmount.toFixed(3)}`, boxXx + 2, y + 5);

            // Right side (split into label/value)
            doc.text("Balance Amount ", colSplitLeft + 3, y + 5);
            doc.text(`${balance}`, boxXx + boxWidthx - 3, y + 5, { align: "right" });
            y += boxHeight;



            // === Amount in Words + Remarks + Declaration Section ===
            const sectionTop = y;
            const boxXa = 5;
            const boxWidtha = pageWidth - 10;
            let ty = sectionTop + 7;

            // === 1️⃣ Amount in Words ===
            doc.setFont("times", "normal").setFontSize(10);
            const labelText = "Amount Chargeable in words:";
            doc.text(labelText, boxXa + 2, ty);

            doc.setFont("times", "bold");
            const amountInWords = ConvertAmountToRialBaisa(balance);
            const amountX = boxXa + 2 + doc.getTextWidth(labelText) + 3;
            doc.text(` ${amountInWords}`, amountX, ty);

            ty += 8;

            // === 2️⃣ Remarks ===
            doc.setFont("times", "bold").setFontSize(10);
            doc.text("Remarks", boxXa + 2, ty);
            ty += 6;

            doc.setFont("times", "normal").setFontSize(10);
            const remarksText = `${header.Remarks1 || ""} ${header.Remarks2 || ""}`.trim();
            if (remarksText) {
                const remarksLines = doc.splitTextToSize(remarksText, boxWidtha - 10);
                doc.text(remarksLines, boxXa + 4, ty);
                ty += remarksLines.length * 4;
            } else {
                ty += 4;
            }

            // === 3️⃣ Declaration ===
            doc.setFont("times", "bold").setFontSize(10);
            doc.text("Declaration", boxXa + 2, ty + 3);
            ty += 8;

            doc.setFont("times", "normal").setFontSize(10);
            const declarationLines = [
                "1. Insurance of cargo by client.",
                "2. All commercial documents attached with invoice to be kept on record for 5 years from the date of custom clearance, to be",
                "   submitted to DG Customs on demand."
            ];
            doc.text(declarationLines, boxXa + 4, ty);

            // calculate total height based on content
            const lineHeight = 4;
            const bottomPadding = 4;
            ty += declarationLines.length * lineHeight + bottomPadding;

            // Draw the box dynamically
            const sectionHeight = ty - sectionTop;
            doc.rect(boxXa, sectionTop, boxWidtha, sectionHeight);

            // ===== SIGNATURE SECTION =====
            y = sectionTop + sectionHeight;

            const margin = 5;
            const leftWidth = 70; // Left box width
            const rightWidth = pageWidth - 2 * margin - leftWidth;
            const topHeight = 12;
            const bottomHeight = 12;

            // === LEFT BOX ===
            doc.rect(margin, y, leftWidth, topHeight + bottomHeight);
            doc.setFont("times", "bold").setFontSize(10);
            doc.text("Customers Seal and Signature", margin + 5, y + topHeight + 5);

            // === RIGHT BOX ===
            const rightX = margin + leftWidth;
            doc.rect(rightX, y, rightWidth, topHeight); // top right

            // Set font and place text inside the box (left aligned with small padding)
            doc.setFont("times", "bold").setFontSize(10);
            doc.text("For Rashid Al Hadi Trading", rightX + 5, y + 7); // ✅ left-aligned inside column

            // === Bottom right 3-column section ===
            const bottomY = y + topHeight;
            doc.rect(rightX, bottomY, rightWidth, bottomHeight);

            const colW = rightWidth / 3;


            doc.setFont("times", "bold").setFontSize(9);
            // Move text slightly lower (was +7 → now +9)
            const labelYOffset = 10;

            doc.text("Prepared by", rightX + colW / 2, bottomY + labelYOffset, { align: "center" });
            doc.text("Verified By", rightX + colW * 1.5, bottomY + labelYOffset, { align: "center" });
            doc.text("Authorized Signatory", rightX + colW * 2.5, bottomY + labelYOffset, { align: "center" });

            // // ===== OPEN & PRINT =====
            const blob = doc.output('blob');
            const blobUrl = URL.createObjectURL(blob);
            const printWindow = window.open(blobUrl, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.focus();
                    printWindow.print();
                };
            }

        } catch (err) {
            console.error("Error generating invoice:", err);
        }
    };


    const NumberToWords = (num) => {
        const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const teens = ['Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', 'Ten', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const convert = (num) => {
            if (num === 0) return '';
            if (num < 10) return units[num];
            if (num === 10) return tens[1];
            if (num < 20) return teens[num - 11];
            if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + units[num % 10] : '');
            if (num < 1000) return units[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + convert(num % 100) : '');
            if (num < 100000) return convert(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 !== 0 ? ' ' + convert(num % 1000) : '');
            if (num < 10000000) return convert(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 !== 0 ? ' ' + convert(num % 100000) : '');
            return 'Number is too large';
        };

        if (num === 0) return 'Zero';
        if (num < 0) return 'Minus ' + convert(Math.abs(num)).trim();
        return convert(num).trim();
    };

    const ConvertAmountToRialBaisa = (amount) => {
        const numericAmount = parseFloat(amount);

        if (isNaN(numericAmount)) return 'Invalid Amount';

        const isNegative = numericAmount < 0;
        const absAmount = Math.abs(numericAmount);

        const formattedAmount = absAmount.toFixed(3); // keep 3 decimals
        const [rials, baisa] = formattedAmount.split('.');

        let words = `${NumberToWords(parseInt(rials))} Rial`;
        if (baisa && parseInt(baisa) > 0) {
            words += ` And ${NumberToWords(parseInt(baisa))} Baisa`;
        }

        return (isNegative ? 'Minus ' : '') + words + ' Only';
    };

    // For Container Section
    const [modalDataContainer, setModalDataContainer] = useState({
        SlNo: '0',
        ContainerNumber: '',
        SourceId: '',
        SourceName: '',
        DestinationId: '',
        DestinationName: '',
        Remarks: '',
    });

    const handleModalInputChangeContainer = (event) => {
        const { name, value } = event.target;
        setModalDataContainer((prevData) => ({
            ...prevData,
            [name]: value,
        }));

    };

    const [errorMessageModel, setErrorMessageModel] = useState('');
    const handleAddContainer = () => {
        let isValid = true;
        let errors = "";
        setErrorMessageModel(errors);

        // Validation for Source
        if (!modalDataContainer.SourceId) {
            isValid = false;
            errors = "Source is required";
        }
        // Validation for Destination
        if (!modalDataContainer.DestinationId) {
            isValid = false;
            errors = "Destination is required";
        }

        if (isValid) {
            const updatedContainerdetails = [...containerItems, modalDataContainer];
            setContainerItems(updatedContainerdetails);

            setModalDataContainer({
                SlNo: '0',
                ContainerNumber: '',
                SourceId: '',
                SourceName: '',
                DestinationId: '',
                DestinationName: '',
                Remarks: '',
            });
            setErrorMessageModel('');
            setSelectedDestination("");
            setSelectedSource("");

        } else {

            console.log("Validation errors:", errors);
            // setErrorMessageModel(errors);
        }
    };

    // Update field values
    const handleEditChange = (index, field, value) => {
        setContainerItems((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        );
    };

    // Delete record
    const handleDelete = (index) => {
        setContainerItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleCancel = (index) => {
        setContainerItems((prevData) =>
            prevData.map((row, i) =>
                i === index ? { ...row, isEditing: false } : row
            )
        );
    };

    // For Expense Section
    const [modalDataExpense, setModalDataExpense] = useState({
        SlNo: '0',
        IncomeExpenseId: '',
        IncomeExpenseName: '',
        Amount: '0',
        VatRate: '0',
        VatAmount: '0',
        TotalAmount: '0',
        Remarks: '',
    });

    const handleModalInputChangeExpense = (e) => {
        const { name, value } = e.target;
        const updatedData = {
            ...modalDataExpense,
            [name]: value,
        };

        // Auto calculate VAT & Total if Amount or VatRate changes
        const amount = parseFloat(updatedData.Amount) || 0;
        const vatRate = parseFloat(updatedData.VatRate) || 0;
        const vatAmount = (amount * vatRate) / 100;
        const totalAmount = amount + vatAmount;

        updatedData.VatAmount = parseFloat(vatAmount.toFixed(3));
        updatedData.TotalAmount = parseFloat(totalAmount.toFixed(3));

        setModalDataExpense(updatedData);
    };

    const handleAddExpense = () => {
        let isValid = true;
        let errors = "";
        setErrorMessageModel(errors);
        // Validation for Expense
        if (!modalDataExpense.IncomeExpenseId) {
            isValid = false;
            errors = "Expense is required";
        }
        // Validation for Amount
        if (!modalDataExpense.Amount) {
            isValid = false;
            errors = "Amount is required";
        }
        if (isValid) {
            const updatedExpensedetails = [...expenseItems, modalDataExpense];
            setExpenseItems(updatedExpensedetails);
            setModalDataExpense({
                SlNo: '0',
                IncomeExpenseId: '',
                IncomeExpenseName: '',
                Amount: '0',
                VatRate: '0',
                VatAmount: '0',
                TotalAmount: '0',
                Remarks: '',
            });
            setErrorMessageModel('');
            setSelectedExpense("");
            const Amount = updatedExpensedetails.reduce((sum, item) => sum + parseFloat(item.Amount), 0);
            const VatAmount = updatedExpensedetails.reduce((sum, item) => sum + parseFloat(item.VatAmount), 0);
            const TotalAmount = updatedExpensedetails.reduce((sum, item) => sum + parseFloat(item.TotalAmount), 0);

            totalCalculation(Amount, VatAmount, TotalAmount);
        } else {
            console.log("Validation errors:", errors);
            // setErrorMessageModel(errors);
        }


    };

    const handleLoadingExpense = (rowIndex) => {
        const currentContainer = containerItems[rowIndex];

        // Build expense object
        const baseExpenseData = {
            IncomeExpenseId: 1,
            IncomeExpenseName: 'Loading Charge',
            Remarks: `${currentContainer.SourceName || ''}-${currentContainer.DestinationName || ''}-${currentContainer.Remarks || ''}`,
            //Remarks: `${currentContainer.SourceName || ''}-${currentContainer.DestinationName || ''}${currentContainer.Remarks ? ` (${currentContainer.Remarks})` : ''}`,
            ContainerId: rowIndex,
        };

        const newExpense = {
            ...baseExpenseData,
            Amount: '0',
            VatRate: '0',
            VatAmount: '0',
            TotalAmount: '0',
        };

        // Check if expense already exists
        const existingIndex = expenseItems.findIndex(
            (item) => item.ContainerId === rowIndex
        );

        let updatedExpenses = [];

        if (existingIndex !== -1) {
            // Update existing expense
            updatedExpenses = expenseItems.map((item, idx) =>
                idx === existingIndex ? { ...item, ...baseExpenseData } : item
            );
        } else {
            // Add new expense
            updatedExpenses = [...expenseItems, newExpense];
        }

        // Update state and totals
        setExpenseItems(updatedExpenses);
        setErrorMessageModel('');
        setSelectedExpense('');

        const totalAmount = updatedExpenses.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);
        const totalVat = updatedExpenses.reduce((sum, item) => sum + parseFloat(item.VatAmount || 0), 0);
        const grandTotal = updatedExpenses.reduce((sum, item) => sum + parseFloat(item.TotalAmount || 0), 0);

        totalCalculation(totalAmount, totalVat, grandTotal);
    };

    const totalCalculation = (Amount, VatAmount, TotalAmount, items = advanceItems) => {
        // ✅ Calculate total advance = sum of (AdvanceAmount - UsedAmount) for checked items only
        const totalAdvanceAmount = items.reduce((sum, item) => {
            if (item.isChecked) {
                const advance = Number(item.AdvanceAmount) || 0;
                const used = Number(item.UsedAmount) || 0;
                return sum + (advance - used);
            }
            return sum;
        }, 0);

        // ✅ Round to 3 decimals
        const roundedTotal = Number((totalAdvanceAmount + formData.PrevAdvanceAmount).toFixed(3));

        // Convert TotalAmount safely
        let total = parseFloat(TotalAmount) || 0;

        // Advance amount logic
        let advanceAmt = roundedTotal;//total > roundedTotal ? roundedTotal : total;

        // Balance amount
        let balanceAmt = total - advanceAmt;

        setFormData((prevData) => ({
            ...prevData,
            TaxableAmount: parseFloat(Amount || 0).toFixed(3),
            VatAmount: parseFloat(VatAmount || 0).toFixed(3),
            TotalAmount: total.toFixed(3),
            AdvanceAmount: advanceAmt.toFixed(3),
            BalanceAmount: balanceAmt.toFixed(3),
            CreatedBy: userId,
        }));
    };

    // Enable edit mode
    const handleEditExpense = (index, field, value) => {
        setExpenseItems((prev) => {
            const updatedItems = prev.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            );

            // get the edited item
            const item = updatedItems[index];
            const amount = parseFloat(item.Amount) || 0;
            const vatRate = parseFloat(item.VatRate) || 0;

            const vatAmount = (amount * vatRate) / 100;
            const totalAmount = amount + vatAmount;

            updatedItems[index] = {
                ...item,
                VatAmount: parseFloat(vatAmount.toFixed(3)),
                TotalAmount: parseFloat(totalAmount.toFixed(3)),
            };

            // recalc totals with rounding
            const Amount = updatedItems.reduce(
                (sum, item) => sum + (parseFloat(item.Amount) || 0),
                0
            );
            const VatAmount = updatedItems.reduce(
                (sum, item) => sum + (parseFloat(item.VatAmount) || 0),
                0
            );
            const TotalAmount = updatedItems.reduce(
                (sum, item) => sum + (parseFloat(item.TotalAmount) || 0),
                0
            );

            // push to your form total calculation
            totalCalculation(
                parseFloat(Amount.toFixed(3)),
                parseFloat(VatAmount.toFixed(3)),
                parseFloat(TotalAmount.toFixed(3))
            );



            return updatedItems;
        });
    };

    // Delete record
    const handleDeleteExpense = (index) => {
        setExpenseItems((prev) => {
            const updatedExpensedetails = prev.filter((_, i) => i !== index);

            // recalc totals with rounding
            const Amount = updatedExpensedetails.reduce(
                (sum, item) => sum + (parseFloat(item.Amount) || 0),
                0
            );
            const VatAmount = updatedExpensedetails.reduce(
                (sum, item) => sum + (parseFloat(item.VatAmount) || 0),
                0
            );
            const TotalAmount = updatedExpensedetails.reduce(
                (sum, item) => sum + (parseFloat(item.TotalAmount) || 0),
                0
            );

            totalCalculation(
                parseFloat(Amount.toFixed(3)),
                parseFloat(VatAmount.toFixed(3)),
                parseFloat(TotalAmount.toFixed(3))
            );

            return updatedExpensedetails;
        });
    };

    // Expand/Collapse state    
    const [isExpanded, setIsExpanded] = useState(true);
    const [isExpandedExpense, setIsExpandedExpense] = useState(true);

    // New Advance 
    const [newadvanceDialogOpen, setNewAdvanceDialogOpen] = useState(false);
    const [invoicedeatils, setInvoicedeatils] = useState([]);
    const [newformData, setNewFormData] = useState({
        CompanyId: companyId,
        BillDate: '',
        BillNumber: 0,
        MasterId: '',
        CreatedBy: userId,
        isSubmit: false,
        IsType: true,
    });

    const handleNewChange = (event) => {
        const { name, value, type, checked } = event.target;

        setNewFormData((prevData) => ({
            ...prevData,
            [name]: name === "IsType"
                ? value === "1"
                : name === "PaymentType"
                    ? Number(value) // Ensure PaymentType is stored as a number
                    : type === "checkbox"
                        ? checked
                        : value,
        }));

        if (name === "Amount" && invoicedeatils.length > 0) {
            const distributed = distributeAmountAcrossBills(Number(value), invoicedeatils);
            setInvoicedeatils(distributed);
        }
        let ispaymentType = true;

        if (name === "IsType") {
            ispaymentType = value === "1";
            setSelectedPaymentType(ispaymentType);
            fetchData('PaymentAndReceiptApi', `/MaxBillno/${branchId}/${companyId}/${ispaymentType}`, token)
                .then((response) => response.response_data)
                .then((data) => {
                    const currentDate = new Date();
                    const year = currentDate.getFullYear();
                    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                    const day = String(currentDate.getDate()).padStart(2, '0');
                    const formattedDate = `${year}-${month}-${day}`;

                    setNewFormData(prevData => ({
                        ...prevData,
                        BillNumber: data.BillNumber,
                        BillDate: formattedDate,
                        FinYear: data.FinYear,
                        BranchId: branchId,
                        CompanyId: companyId,
                    }));
                })
                .catch((error) => {
                    console.error('Error fetching Max Billno:', error);
                });
        } else {

        }
    };

    useEffect(() => {
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        setNewFormData((prevData) => ({
            ...prevData,
            CompanyId: companyId,
            BillDate: formattedDate,
            ChequeDate: formattedDate,
            // PaymentType: selectedPaymentmethodType.value,
            PaymentType: 1,
        }));
    }, []);

    const [selectedPaymentType, setSelectedPaymentType] = useState("1");

    //customer Payment Method 
    const [paymentmethodOptions, setPaymentmethodOptions] = useState([]);
    const [selectedPaymentmethodType, setSelectedPaymentmethodType] = useState("");

    const fetchPaymentmethodTypes = async () => {
        try {
            const response = await fetchData('PaymentAndReceiptApi', `/PaymentModes/${companyId}`, token);
            if (response.responseCode === '1111') {

                const options = response.response_data.map(method => ({
                    value: method.Id,
                    label: method.Name
                }));
                setPaymentmethodOptions(options);
                setShowBankField(true);
            }
        } catch (error) {
            console.error('Error fetching PaymentModes:', error);
        }
    };

    const handlePaymentTypeChange = (selectedOption) => {
        setSelectedPaymentmethodType(selectedOption);
        setNewFormData((prevData) => ({
            ...prevData,
            PaymentType: selectedOption.value,
        }));
        if (["Cheque", "RTGS", "NEFT", "UPI"].includes(selectedOption.label)) {
            setShowBankField(false);
        } else {
            setShowBankField(true);
        }
    };

    useEffect(() => {
        fetchPaymentmethodTypes();
    }, []);

    //FromBank
    const [bankOptions, setBankOptions] = useState([]);
    const [selectedBank, setSelectedBank] = useState("");

    const fetchBanks = async () => {
        try {
            const response = await fetchData('PaymentAndReceiptApi', `/Banks/${companyId}`, token);
            if (response.responseCode === '1111') {

                const options = response.response_data.map(method => ({
                    value: method.Id,
                    label: method.Name
                }));
                setBankOptions(options);
            }
        } catch (error) {
            console.error('Error fetching Bank:', error);
        }
    };

    const handleBankChange = (selectedOption) => {
        setSelectedBank(selectedOption);
    };

    useEffect(() => {
        fetchBanks();
    }, []);

    //ToBank
    const [tobankOptions, setToBankOptions] = useState([]);
    const [selectedToBank, setSelectedToBank] = useState("");

    const fetchToBanks = async () => {
        try {
            const response = await fetchData('PaymentAndReceiptApi', `/Banks/${companyId}`, token);
            if (response.responseCode === '1111') {

                const options = response.response_data.map(method => ({
                    value: method.Id,
                    label: method.Name
                }));
                setToBankOptions(options);
            }
        } catch (error) {
            console.error('Error fetching Bank:', error);
        }
    };

    const handleToBankChange = (selectedOption) => {
        setSelectedToBank(selectedOption);
    };

    useEffect(() => {
        fetchToBanks();
    }, []);

    const [showBankField, setShowBankField] = useState(true);
    const fetchmaxbillno = (Inputstring) => {
        // Use the parameter directly, no need to rely on formData here
        fetchData('PaymentAndReceiptApi', `/MaxBillno/${branchId}/${companyId}/${Inputstring}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                const currentDate = new Date();
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                const formattedDate = `${year}-${month}-${day}`;

                // Update formData with the fetched data
                setNewFormData((prevData) => ({
                    ...prevData,
                    BillNumber: data.BillNumber,
                    BillDate: formattedDate,
                    FinYear: data.FinYear,
                    BranchId: branchId,
                    CompanyId: companyId,
                }));
            })
            .catch((error) => {
                console.error('Error fetching Max Billno:', error);
            });
    };

    useEffect(() => {
        fetchmaxbillno(true);
    }, []);

    // Handle customer selection
    const handleNewCustomerSelect = (selectedOption) => {
        setSelectedNewCustomer(selectedOption);
        setFormData((prevData) => ({
            ...prevData,
            CustomerId: selectedOption ? selectedOption.value : '',
            CreatedBy: userId,
        }));
    };

    useEffect(() => {
        fetchCustomerData();
        fetchConsigneeData();
    }, []);

    //FOR GET Customer  Details FROM API
    const [NewcustomerOptions, setNewCustomerOptions] = useState([]);
    const [selectedNewCustomer, setSelectedNewCustomer] = useState(null);

    const fetchCustomerData = () => {
        fetchData('PaymentAndReceiptApi', `/Customer/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                // Transform the data into the format that react-select expects (label and value)                
                const options = data.map((customer) => ({
                    label: customer.Name,
                    value: customer.Id,
                }));
                setNewCustomerOptions(options);

            })
            .catch((error) => {
                console.error('Error fetching Customer data:', error);
            });
    };

    const [NewConsigneeOptions, setNewConsigneeOptions] = useState([]);
    const [selectedNewConsignee, setSelectedNewConsignee] = useState(null);
    //FOR GET Consignee  Details FROM API
    const fetchConsigneeData = () => {
        fetchData('PaymentAndReceiptApi', `/Consignee/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                // Transform the data into the format that react-select expects (label and value)                
                const options = data.map((Consignee) => ({
                    label: Consignee.Name,
                    value: Consignee.Id,
                }));
                setNewConsigneeOptions(options);

            })
            .catch((error) => {
                console.error('Error fetching Consignee data:', error);
            });
    };

    const distributeAmountAcrossBills = (amount, invoiceDetails) => {
        let remaining = amount;
        return invoiceDetails.map(inv => {
            const billAmt = Number(inv.Amount || 0);
            const alreadyPaid = Number(inv.PaidAmount || 0);
            const pending = billAmt - alreadyPaid;

            let payNow = 0;
            let balance = pending;

            if (remaining > 0) {
                if (remaining >= pending) {
                    payNow = pending;   // full settle this bill
                    balance = 0;
                    remaining -= pending;
                } else {
                    payNow = remaining; // partial settle this bill
                    balance = pending - remaining;
                    remaining = 0;
                }
            }

            return {
                ...inv,
                PaidNow: payNow,             // new field
                NewBalance: balance          // new field
            };
        });
    };

    const handleNewConsigneeChange = (selectedOption) => {
        setSelectedNewConsignee(selectedOption);

        setNewFormData((prevData) => ({
            ...prevData,
            ConsigneeId: selectedOption ? selectedOption.value : '',
            CreatedBy: userId,
        }));

        console.log("Selected Consignee:", selectedOption);

        if (selectedOption && selectedNewCustomer) {
            fetchData(
                'PaymentAndReceiptApi',
                `/CustomerReceiptBill/${companyId}/${branchId}/${selectedNewCustomer.value}/${selectedOption.value}`,
                token
            )
                .then((response) => {
                    if (response.responseCode === '1111') {
                        const updatedInvoicedetailsSorted = response.response_data;

                        const totalPendingBalance = updatedInvoicedetailsSorted.reduce((total, invdetail) => {
                            return total + Number(invdetail.BalanceAmount || 0);
                        }, 0);

                        // Distribute initially with full pending
                        const distributed = distributeAmountAcrossBills(totalPendingBalance, updatedInvoicedetailsSorted);

                        setNewFormData((prevData) => ({
                            ...prevData,
                            TotalBalAmt: totalPendingBalance,
                            Amount: totalPendingBalance,  // auto-fill
                        }));

                        setInvoicedeatils(distributed);
                    }

                })
                .catch((error) => {
                    console.error('Error fetching invoice details:', error);
                });
        }
    };

    const submitData = async (e) => {
        setIsLoading(true);

        if (!formData.CustomerId || formData.CustomerId === 0) {
            setErrorMessage('Customer is required!');
            setIsLoading(false);
            return;
        }

        if (!formData.ConsigneeId || formData.ConsigneeId === 0) {
            setErrorMessage('Consignee is required!');
            setIsLoading(false);
            return;
        }

        // Validation for non-cash payment types
        if (selectedPaymentmethodType && selectedPaymentmethodType.label !== 'Cash') {
            if (!newformData.ChequeNumber) {
                setErrorMessage('Reference No is required for non-cash payments!');
                setIsLoading(false);
                return;
            }
            if (!newformData.ChequeDate) {
                setErrorMessage('Reference Date is required for non-cash payments!');
                setIsLoading(false);
                return;
            }
            if (!newformData.chequeDetails) {
                setErrorMessage('Reference Details are required for non-cash payments!');
                setIsLoading(false);
                return;
            }
            if (!selectedBank) {
                setErrorMessage('Bank selection is required for non-cash payments!');
                setIsLoading(false);
                return;
            }
        }

        const updatedFormData = {
            ...newformData,
            CreatedBy: userId,
            CustomerId: selectedOption.value,
            ConsigneeId: ConsigneeselectedOption.value,
            FromBankId: selectedBank ? selectedBank.value : null,
            ToBankId: selectedToBank ? selectedToBank.value : null,
        };

        const createData = {
            PaymentAndReceipt: updatedFormData,
            PaymentAndReceiptInvoiceDetails: invoicedeatils,
        };

        try {
            // ✅ Save payment/receipt
            await postData('PaymentAndReceiptApi', createData, token);

            // ✅ Fetch updated advance list
            const advResponse = await fetchData(
                'InvoiceApi',
                `/AdvanceAmount/${formData.CustomerId}/${formData.ConsigneeId}`,
                token
            );

            if (advResponse.responseCode === '1111') {
                const data = advResponse.response_data;

                setAdvanceItems(data);

                // ✅ Calculate total advance = sum of (AdvanceAmount - UsedAmount)
                const totalAdvanceAmount = data.reduce((sum, item) => {
                    const advance = Number(item.AdvanceAmount) || 0;
                    const used = Number(item.UsedAmount) || 0;
                    return sum + (advance - used);
                }, 0);

                // ✅ Round to 3 decimals
                const roundedTotal = Number(totalAdvanceAmount.toFixed(3));

                // ✅ Update form state with total
                setFormData(prevData => ({
                    ...prevData,
                    TotalAdvanceAmount: roundedTotal,
                }));
            } else {
                console.error('Unexpected response code:', advResponse.responseCode);
            }

            setErrorMessage('');
            setNewAdvanceDialogOpen(false); // ✅ directly close modal

        } catch (error) {
            console.error(error);
            setErrorMessage('Something went wrong. Please try again.');
        } finally {
            setIsLoading(false); // ✅ always stop loader
        }
    };

    const toggleModalAdvance = () => {
        setNewAdvanceDialogOpen(!newadvanceDialogOpen);
    };


    return (
        <Fragment>
            <div className="col-span-2 md:col-span-6 flex flex-col md:flex-row justify-between items-center mb-4 gap-4" style={{ marginTop: '-40px' }}>
                <h6 className="uppercase tracking-wide font-serif text-black mt-2">
                    Consignment  Edit
                </h6>

                <div className="flex items-center gap-4 mt-2 md:mt-0">
                    {/* Save Button */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        style={{ height: "25px", borderRadius: "10px" }}
                    >
                        {isLoading ? <CircularProgress size={22} color="inherit" /> : "Update"}
                    </Button>

                    {/* Back to List Button */}
                    <Button
                        variant="contained"
                        color="error"
                        component={Link}
                        to={{
                            pathname: "/InvoiceIndex",
                            state: { from: "/InvoiceEdit" },
                        }}
                        onClick={() => {
                            localStorage.setItem("shouldReload", "true");
                        }}
                        style={{ height: "25px", borderRadius: "10px", marginRight: "5px" }}
                    >
                        Back To List
                    </Button>
                </div>
            </div>

            {/* Advance Receipts Dialog */}

            <Dialog
                open={advanceDialogOpen}
                onClose={() => setAdvanceDialogOpen(false)}
                maxWidth={false}   // disable default breakpoints
                PaperProps={{
                    sx: { width: "700px", borderRadius: "12px", boxShadow: 6 },
                }}
            >
                <div className="flex items-end justify-end gap-4 mt-2 md:mt-0">
                    <Button
                        variant="contained"
                        color="error"
                        style={{ height: "25px", borderRadius: "10px", marginRight: "5px" }}
                        onClick={() => setNewAdvanceDialogOpen(true)}
                    >
                        New Advance
                    </Button>
                </div>

                <DialogTitle
                    sx={{
                        textAlign: "center",
                        color: "darkblue",
                        textTransform: "uppercase",
                        fontSize: "15px",
                        fontWeight: "bold",
                        pb: 1,
                    }}
                >
                    Advance Receipts
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                <TableCell padding="checkbox" />
                                <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                                    Bill Date
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "14px" }}>
                                    Bill Number
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold", fontSize: "14px" }}
                                >
                                    Balance Amount
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {advanceItems.map((row, index) => {
                                const balance = row.AdvanceAmount - row.UsedAmount;
                                return (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isRowChecked(row.BillNumber)}
                                                onChange={() => handleCheckboxChange(row)}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: "14px", py: 0.7 }}>
                                            {formatDateForInput(row.BillDate)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: "14px", py: 0.7 }}>
                                            {row.BillNumber}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: "14px", py: 0.7 }}>
                                            {balance.toFixed(3)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </DialogContent>
            </Dialog>


            <Dialog
                open={newadvanceDialogOpen}
                onClose={() => setNewAdvanceDialogOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    style: { width: "90%", maxWidth: "90%" },
                }}
            >
                <DialogTitle
                    style={{
                        textAlign: "center",
                        color: "darkblue",
                        textTransform: "uppercase",
                        fontSize: "12.5px",
                        fontWeight: "bold",
                    }}
                >
                    New Advance Receipts
                </DialogTitle>

                <DialogContent>
                    <div
                        className="w-full p-3 rounded-lg shadow-md"
                        style={{
                            backgroundColor: "whitesmoke",
                            marginTop: "-15px",
                            boxShadow:
                                "0 6px 10px rgba(0, 0, 0, 0.25), 0 12px 25px rgba(0, 0, 0, 0.2)",
                        }}
                    >
                        {/* Row 1 */}
                        <div className="grid grid-cols-12 gap-2 mb-2">
                            <div className="col-span-3">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="supplierName">
                                    Customer
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-black-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={selectedOption?.label || ""}
                                    type="text"
                                    placeholder="Customer"
                                    readOnly
                                    style={{ fontSize: '11.5px' }}
                                />
                            </div>

                            <div className="col-span-3">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="supplierName">
                                    Consignee
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-black-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={ConsigneeselectedOption?.label || ""}
                                    type="text"
                                    placeholder="Consignee"
                                    readOnly
                                    style={{ fontSize: '11.5px' }}
                                />
                            </div>

                            <div className="col-span-2">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="billDate">
                                    Bill Date
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={formatDateForInput(newformData.BillDate)}
                                    onChange={handleNewChange}
                                    type="date"
                                    name='BillDate'
                                    style={{ height: '30px' }}
                                />
                            </div>

                            <div className="col-span-1">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="billNumber">
                                    Bill No
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={`${newformData.BillNumber || ''}`}
                                    onChange={handleNewChange}
                                    type="text"
                                    placeholder="Bill Number"
                                    name='BillNumber'
                                    readOnly
                                    style={{ height: '30px' }}
                                />
                            </div>

                            <div className="col-span-1">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="supplierName">
                                    Pay Mode
                                </label>

                                <Select
                                    value={selectedPaymentmethodType || { value: 'cash', label: 'Cash' }}  // Default to "Cash" if not selected
                                    onChange={handlePaymentTypeChange}
                                    options={paymentmethodOptions}
                                    placeholder="PaymentOption"
                                    isSearchable
                                    //Keep the dorpdown outer
                                    menuPortalTarget={document.body}   // 👈 forces dropdown outside container
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: "30px",
                                            height: "30px",
                                            fontSize: "14px",
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            height: "30px",
                                            padding: "0 6px",
                                        }),
                                        indicatorsContainer: (base) => ({
                                            ...base,
                                            height: "30px",
                                        }),
                                        menuPortal: (base) => ({ ...base, zIndex: 2000 }), // 👈 make sure it overlays dialog
                                        menu: (provided) => ({
                                            ...provided,
                                            fontSize: "14px",
                                        }),
                                    }}
                                />
                            </div>

                            <div className="col-span-2">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="billNumber">
                                    Amount
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={
                                        newformData.Amount != null && newformData.Amount !== ''
                                            ? Number(newformData.Amount)
                                            : ''
                                    }
                                    onChange={handleNewChange}
                                    type="number"
                                    placeholder="Amount"
                                    name='Amount'
                                    style={{ textAlign: 'right', fontWeight: 'bold', height: '30px' }}
                                />
                            </div>

                        </div>

                        <div className="w-full mb-2 flex">

                            <div className="w-1/5 mr-2">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="billNumber">
                                    {selectedPaymentmethodType && selectedPaymentmethodType.value === 3 ? 'From Cheque Bank' : 'From Bank'}
                                    <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    value={selectedBank || { value: '', label: 'Select Bank' }}
                                    onChange={handleBankChange}
                                    options={bankOptions}
                                    placeholder="Select Bank"
                                    isSearchable
                                    isDisabled={showBankField || (selectedPaymentmethodType && selectedPaymentmethodType.value === '1')}
                                    menuPortalTarget={document.body}   // 👈 forces dropdown outside container
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: "30px",
                                            height: "30px",
                                            fontSize: "14px",
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            height: "30px",
                                            padding: "0 6px",
                                        }),
                                        indicatorsContainer: (base) => ({
                                            ...base,
                                            height: "30px",
                                        }),
                                        menuPortal: (base) => ({ ...base, zIndex: 2000 }), // 👈 make sure it overlays dialog
                                        menu: (provided) => ({
                                            ...provided,
                                            fontSize: "14px",
                                        }),
                                    }}
                                />
                            </div>
                            <div className="w-1/5 mr-2">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="billNumber">
                                    {selectedPaymentmethodType && selectedPaymentmethodType.value === 3 ? 'To Cheque Bank' : 'To Bank'}
                                    <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    value={selectedToBank || { value: '', label: 'Select Bank' }}
                                    onChange={handleToBankChange}
                                    options={tobankOptions}
                                    placeholder="Select Bank"
                                    isSearchable
                                    isDisabled={showBankField || (selectedPaymentmethodType && selectedPaymentmethodType.value === '1')}
                                    menuPortalTarget={document.body}   // 👈 forces dropdown outside container
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: "30px",
                                            height: "30px",
                                            fontSize: "14px",
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            height: "30px",
                                            padding: "0 6px",
                                        }),
                                        indicatorsContainer: (base) => ({
                                            ...base,
                                            height: "30px",
                                        }),
                                        menuPortal: (base) => ({ ...base, zIndex: 2000 }), // 👈 make sure it overlays dialog
                                        menu: (provided) => ({
                                            ...provided,
                                            fontSize: "14px",
                                        }),
                                    }}
                                />
                            </div>
                            <div className="w-1/6 mr-2">

                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="billNumber">
                                    {selectedPaymentmethodType && selectedPaymentmethodType.value === 3 ? 'Cheque No' : 'Reference No'}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-black-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={newformData.ChequeNumber}
                                    type="text"
                                    placeholder=""
                                    name="ChequeNumber"
                                    onChange={handleNewChange}
                                    style={{ height: '30px' }}
                                    disabled={showBankField || (selectedPaymentmethodType && selectedPaymentmethodType.value === '1')}
                                />
                            </div>

                            <div className="w-1/8 mr-2">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="billNumber">
                                    {selectedPaymentmethodType && selectedPaymentmethodType.value === 3 ? 'Cheque Date' : 'Reference Date'}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-black-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={formatDateForInput(newformData.ChequeDate)}
                                    type="date"
                                    placeholder=""
                                    name="ChequeDate"
                                    onChange={handleNewChange}
                                    style={{ height: '30px' }}
                                    disabled={showBankField || (selectedPaymentmethodType && selectedPaymentmethodType.value === '1')}
                                />
                            </div>

                            <div className="w-1/4 mr-2">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="billNumber">
                                    {selectedPaymentmethodType && selectedPaymentmethodType.value === 3 ? 'Cheque Details' : 'Reference Details'}
                                    <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-black-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={newformData.chequeDetails}
                                    type="text"
                                    placeholder=""
                                    name="chequeDetails"
                                    onChange={handleNewChange}
                                    style={{ height: '30px' }}
                                    disabled={showBankField || (selectedPaymentmethodType && selectedPaymentmethodType.value === '1')}
                                />
                            </div>

                            <div className="w-1/4 mr-2">
                                <label className="block uppercase tracking-wide text-black text-xs mb-2" htmlFor="billNumber">
                                    Remarks
                                </label>
                                <textarea
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    type="text"
                                    placeholder="Remarks"
                                    name="Remarks"
                                    value={newformData.Remarks}
                                    onChange={handleNewChange}
                                    style={{ height: '30px', width: '100%' }}
                                />
                            </div>

                        </div>

                        <div className="flex mt-4" style={{ justifyContent: 'center' }}>
                            {/* Add Advance Amount Button*/}
                            <button
                                onClick={submitData}
                                style={{
                                    backgroundColor: '#088b11ff',
                                    color: '#ffffff',
                                    padding: '6px 12px',
                                    fontSize: '13px',
                                    height: '32px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'transform 0.15s ease, background-color 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'scale(1.04)';
                                    e.target.style.backgroundColor = '#1e9304ff';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'scale(1)';
                                    e.target.style.backgroundColor = '#09a628ff';
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <CircularProgress size={16} color="inherit" />
                                        Saving...
                                    </>
                                ) : (
                                    "Add Advance"
                                )}
                            </button>

                            {/* Cancel Button */}
                            <button
                                onClick={toggleModalAdvance}
                                style={{
                                    backgroundColor: '#64748b',
                                    color: '#ffffff',
                                    padding: '6px 12px',
                                    fontSize: '13px',
                                    height: '32px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    marginLeft: '8px',
                                    cursor: 'pointer',
                                    transition: 'transform 0.15s ease, background-color 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'scale(1.04)';
                                    e.target.style.backgroundColor = '#475569';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'scale(1)';
                                    e.target.style.backgroundColor = '#64748b';
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {errorMessage && <p className="text-red-500">{errorMessage}</p>}

            <div className="w-full flex flex-wrap"
                style={{ backgroundColor: 'whitesmoke', padding: '7px', borderRadius: '10px', marginTop: '-15px', boxShadow: "0 6px 10px rgba(0, 0, 0, 0.25), 0 12px 25px rgba(0, 0, 0, 0.2)" }}>
                <div className="w-full pr-4" style={{ marginTop: '-0.21%' }}>
                    <div className="w-full mb-2 flex">
                        <div className="w-4/5 mr-2">
                            <label
                                className="block uppercase text-black text-xs mb-1"
                                style={{ fontFamily: "monospace", fontSize: "12.5px" }}
                            >
                                Customer
                                <span
                                    className="ml-2 text-sm text-blue-900 font-semibold cursor-pointer"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px", color: "red" }}
                                    onClick={() => setAdvanceDialogOpen(true)}
                                >
                                    Advance Amt:{" "}
                                    {formData?.TotalAdvanceAmount != null
                                        ? Number(formData.TotalAdvanceAmount).toFixed(3)
                                        : "-"}
                                </span>
                            </label>

                            <AsyncSelect
                                cacheOptions
                                loadOptions={fetchOptions}
                                onInputChange={handleInputChange}
                                onChange={handleCustomerSelect}
                                value={selectedOption}
                                placeholder="Select a customer"
                                isDisabled={true}   // 👈 use isDisabled instead of disabled
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: "30px",
                                        height: "30px",
                                        fontSize: "14px",
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                        padding: "0 6px",
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 1301,
                                        fontSize: "14px",
                                    }),
                                }}
                            />
                        </div>
                        <div className="w-4/5 mr-2">
                            <label className="block uppercase text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}
                            >
                                Consignee
                                <span
                                    className="ml-2 text-sm text-blue-900 font-semibold cursor-pointer"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px", color: "red" }}
                                >
                                    Selected Advance :{" "}
                                    {advanceItems
                                        .reduce((sum, item) => {
                                            if (item.isChecked) {
                                                const advance = Number(item.AdvanceAmount) || 0;
                                                const used = Number(item.UsedAmount) || 0;
                                                return sum + (advance - used);
                                            }
                                            return sum;
                                        }, 0)
                                        .toFixed(3)}
                                </span>

                            </label>

                            <AsyncSelect
                                cacheOptions
                                loadOptions={ConsigneefetchOptions}
                                onInputChange={handleInputChange}
                                onChange={handleConsigneeSelect}
                                value={ConsigneeselectedOption}
                                placeholder="Select a Consignee"
                                isDisabled={true}   // 👈 use isDisabled instead of disabled                      
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: "30px",
                                        height: "30px",
                                        fontSize: "14px",
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                        padding: "0 6px",
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: "27px",
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 1301,
                                        fontSize: "14px",
                                    }),
                                }}
                            />
                        </div>
                        <div className="w-2/5 mr-2">
                            <label className="block uppercase text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Consignment Type
                            </label>
                            <Select
                                value={selectedConsignmentType}
                                onChange={handleConsignmentTypeChange}
                                options={ConsignmentTypeOptions}
                                placeholder="Consignment"
                                isSearchable
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: "30px",
                                        height: "30px",
                                        fontSize: "14px",
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                        padding: "0 6px",
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 1301,
                                        fontSize: "14px",
                                    }),
                                }}
                            />
                        </div>

                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Invoice Date
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={formatDateForInput(formData.InvoiceDate)}
                                onChange={handleChange}
                                type="date"
                                name='InvoiceDate'
                                style={{ height: '30px' }}
                            />
                        </div>

                        <div className="w-2/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Invoice Number
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                //value={formData.InvoiceNumber}
                                value={`${formData.BillPrefix}/${formData.InvoiceNumber}/${formData.InvoiceDate ? new Date(formData.InvoiceDate).getFullYear() : ''}`}
                                onChange={handleChange}
                                type="text"
                                placeholder="Invoice Number"
                                name='InvoiceNumber'
                                style={{ height: '30px', fontSize: '14.5px' }}
                                readOnly
                            />
                        </div>

                        <div className="w-3/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                BL Number
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={formData.BLNumber}
                                onChange={handleChange}
                                type="text"
                                placeholder="BL Number"
                                name='BLNumber'
                                style={{ height: '30px', fontSize: '14px' }}
                                maxLength={50}
                            />
                        </div>

                    </div>

                    <div className="w-full mb-2 flex">
                        <div className="w-3/5 mr-2">
                            <label
                                className="block uppercase text-black text-xs mb-1"
                                style={{ fontFamily: "monospace", fontSize: "12.5px" }}
                            >
                                Remarks 1
                            </label>
                            <textarea
                                className="w-full border rounded text-gray-700 focus:outline-none"
                                name="Remarks1"
                                value={formData.Remarks1}
                                onChange={handleChange}
                                maxLength={200}
                                style={{
                                    fontSize: "13px",
                                    height: "30px",      // default fixed height
                                    padding: "4px 6px",
                                    resize: "vertical",  // ✅ allow drag resize
                                }}
                            />
                        </div>

                        <div className="w-3/5 mr-2">
                            <label
                                className="block uppercase text-black text-xs mb-1"
                                style={{ fontFamily: "monospace", fontSize: "12.5px" }}
                            >
                                Remarks 2
                            </label>
                            <textarea
                                className="w-full border rounded text-gray-700 focus:outline-none"
                                name="Remarks2"
                                value={formData.Remarks2}
                                onChange={handleChange}
                                maxLength={200}
                                style={{
                                    fontSize: "13px",
                                    height: "30px",
                                    padding: "4px 6px",
                                    resize: "vertical",  // ✅ user can drag height
                                }}
                            />
                        </div>

                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Taxable
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={
                                    formData?.TaxableAmount
                                        ? Number(formData.TaxableAmount).toFixed(3)
                                        : "0.000"
                                }
                                onChange={handleChange}
                                type="text"
                                placeholder="TaxableAmount"
                                name='TaxableAmount'
                                style={{ height: '30px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}
                                readOnly
                            />
                        </div>

                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }} >
                                Vat Amount
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={
                                    formData?.VatAmount
                                        ? Number(formData.VatAmount).toFixed(3)
                                        : "0.000"
                                }
                                onChange={handleChange}
                                type="text"
                                placeholder="Vat Amount"
                                name='VatAmount'
                                style={{ height: '30px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}
                                readOnly
                            />
                        </div>

                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Total
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={
                                    formData?.TotalAmount
                                        ? Number(formData.TotalAmount).toFixed(3)
                                        : "0.000"
                                }
                                onChange={handleChange}
                                type="text"
                                placeholder="TotalAmount"
                                name='TotalAmount'
                                style={{ height: '30px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}
                                readOnly
                            />
                        </div>

                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }} >
                                Advance
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={
                                    formData?.AdvanceAmount
                                        ? Number(formData.AdvanceAmount).toFixed(3)
                                        : "0.000"
                                }
                                onChange={handleChange}
                                type="text"
                                placeholder="Advance Amount"
                                name='AdvanceAmount'
                                style={{ height: '30px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}
                                readOnly
                            />
                        </div>

                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Balance
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={
                                    formData?.BalanceAmount
                                        ? Number(formData.BalanceAmount).toFixed(3)
                                        : "0.000"
                                }
                                onChange={handleChange}
                                type="text"
                                placeholder="BalanceAmount"
                                name='BalanceAmount'
                                style={{ height: '30px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold', color: 'red' }}
                                readOnly
                            />
                        </div>

                    </div>
                </div>

            </div>

            <div
                className="w-full flex flex-col"
                style={{
                    backgroundColor: "whitesmoke",
                    padding: "7px",
                    borderRadius: "10px",
                    marginTop: "10px",
                    boxShadow: "0 6px 10px rgba(0, 0, 0, 0.25), 0 12px 25px rgba(0, 0, 0, 0.2)"
                }}
            >
                {/* Header with expand/collapse button */}
                <div className="flex justify-between items-center">
                    <h6
                        className="uppercase tracking-wide font-serif mt-2"
                        style={{ color: "#24054eff", fontSize: '13px' }}
                    >
                        Container List
                    </h6>
                    {errorMessageModel && (
                        <span style={{ color: 'red', fontSize: '13px', marginLeft: '10px' }}>
                            {errorMessageModel}
                        </span>
                    )}

                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-sm text-blue-600 flex items-center gap-1"
                        style={{ color: "#24054eff" }}
                    >
                        {isExpanded ? (
                            <>
                                <ExpandLessIcon size={16} />
                            </>
                        ) : (
                            <>
                                <ExpandMoreIcon size={16} />
                            </>
                        )}
                    </button>
                </div>

                {/* Expandable Section */}
                {isExpanded && (
                    <div className="w-full mt-1">
                        {/* --- your form section --- */}
                        <div className="w-full mb-2 flex items-end gap-1" style={{ marginTop: "-3px" }}>
                            {/* Container Number */}
                            <div className="w-1/5">
                                <label
                                    className="block uppercase text-black text-xs mb-1"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px" }}
                                >
                                    Container Number
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline h-8"
                                    value={modalDataContainer.ContainerNumber}
                                    type="text"
                                    placeholder="Container Number"
                                    name="ContainerNumber"
                                    onChange={handleModalInputChangeContainer}
                                    style={{ height: "30px", fontSize: '14px' }}
                                    maxLength={100}
                                />
                            </div>

                            {/* Source */}
                            <div className="w-1/5">
                                <label
                                    className="block uppercase text-black text-xs mb-1"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px" }}
                                >
                                    Source
                                </label>
                                <Select
                                    value={selectedSource}
                                    onChange={handleSourceChange}
                                    options={SourceOptions}
                                    placeholder="Source Type"
                                    isSearchable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: "30px",   // same as your inputs
                                            height: "30px",
                                            fontSize: "14px",
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            height: "30px",      // keeps text aligned properly
                                            padding: "0 6px",
                                        }),
                                        indicatorsContainer: (base) => ({
                                            ...base,
                                            height: "30px",      // makes dropdown arrow align
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            zIndex: 1301,
                                            fontSize: "14px",
                                        }),
                                    }}
                                />
                            </div>

                            {/* Destination */}
                            <div className="w-1/5">
                                <label
                                    className="block uppercase text-black text-xs mb-1"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px" }}
                                >
                                    Destination
                                </label>
                                <Select
                                    value={selectedDestination}
                                    onChange={handleDestinationChange}
                                    options={DestinationOptions}
                                    placeholder="Destination"
                                    isSearchable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: "30px",   // same as your inputs
                                            height: "30px",
                                            fontSize: "14px",
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            height: "30px",      // keeps text aligned properly
                                            padding: "0 6px",
                                        }),
                                        indicatorsContainer: (base) => ({
                                            ...base,
                                            height: "30px",      // makes dropdown arrow align
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            zIndex: 1301,
                                            fontSize: "14px",
                                        }),
                                    }}
                                />
                            </div>

                            {/* Remarks */}
                            <div className="w-2/5">
                                <label
                                    className="block uppercase text-black text-xs mb-1"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px" }}
                                >
                                    Remarks
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline h-8"
                                    value={modalDataContainer.Remarks}
                                    onChange={handleModalInputChangeContainer}
                                    type="text"
                                    placeholder="Remarks"
                                    name="Remarks"
                                    style={{ height: "30px", fontSize: '14px' }}
                                    maxLength={200}
                                />
                            </div>

                            {/* Add Button */}
                            <div className="w-auto">
                                <button
                                    onClick={handleAddContainer}
                                    className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white font-medium px-2 py-1 rounded-sm shadow-sm transition duration-200 ease-in-out text-sm"
                                    style={{ height: "28px", minWidth: "75px", borderRadius: "5px" }}
                                >
                                    <AddIcon fontSize="inherit" style={{ fontSize: "14px" }} /> Add
                                </button>
                            </div>
                        </div>

                        {/* Table Section */}
                        <div className="w-full pr-4" style={{ marginTop: "-3px" }}>
                            {containerItems.length > 0 && (
                                <div
                                    className="mb-2 flex"
                                    style={{
                                        maxHeight: "450px",
                                        overflowY: "auto",
                                        marginTop: "3px",
                                    }}
                                >
                                    <TableContainer component={Paper}>
                                        <Table stickyHeader>
                                            <TableHead stickyHeader>
                                                <TableRow>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}
                                                    >
                                                        Sl.No
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}>
                                                        Container Number
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}>
                                                        Source
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}
                                                    >
                                                        Destination
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}
                                                    >
                                                        Remarks
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                            textAlign: "center",
                                                        }}
                                                    >

                                                    </TableCell>

                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {containerItems.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={7} align="center" style={{ fontSize: "12px", padding: "10px" }}>
                                                            No containers added yet.
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    containerItems.map((item, index) => {
                                                        // ✅ Use row index as ContainerId
                                                        const existsForCurrent = expenseItems.some(
                                                            (exp) => exp.ContainerId === `${index}`
                                                        );

                                                        return (
                                                            <TableRow key={index} hover>
                                                                {/* Sl.No (not editable) */}
                                                                <TableCell
                                                                    style={{
                                                                        fontSize: "12px",
                                                                        padding: "6px",
                                                                        textAlign: "center",
                                                                        borderRight: "1px solid #D6DAC8",
                                                                        width: "50px",
                                                                    }}
                                                                >
                                                                    {index + 1}
                                                                </TableCell>

                                                                {/* Container Number */}
                                                                <TableCell
                                                                    style={{
                                                                        fontSize: "12px",
                                                                        padding: "6px",
                                                                        borderRight: "1px solid #D6DAC8",
                                                                        width: "200px",
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="text"
                                                                        value={item.ContainerNumber}
                                                                        onChange={(e) =>
                                                                            handleEditChange(index, "ContainerNumber", e.target.value)
                                                                        }
                                                                        style={{ width: "100%", fontSize: "12px", padding: "4px" }}
                                                                        maxLength={100}
                                                                    />
                                                                </TableCell>

                                                                {/* Source (dropdown when editing) */}
                                                                <TableCell
                                                                    style={{
                                                                        fontSize: "12px",
                                                                        padding: "6px",
                                                                        borderRight: "1px solid #D6DAC8",
                                                                        width: "300px",
                                                                    }}
                                                                >
                                                                    <Select
                                                                        value={SourceOptions.find(opt => opt.value === item.SourceId)}
                                                                        onChange={(selected) => {
                                                                            handleEditChange(index, "SourceId", selected.value);
                                                                            handleEditChange(index, "SourceName", selected.label); // store name also
                                                                        }}
                                                                        options={SourceOptions}
                                                                        placeholder="Source"
                                                                        isSearchable
                                                                        menuPortalTarget={document.body}
                                                                        styles={{
                                                                            control: (base) => ({
                                                                                ...base,
                                                                                minHeight: "27px",
                                                                                height: "27px",
                                                                                fontSize: "14px",
                                                                            }),
                                                                            valueContainer: (base) => ({
                                                                                ...base,
                                                                                height: "27px",
                                                                                padding: "0 6px",
                                                                            }),
                                                                            indicatorsContainer: (base) => ({
                                                                                ...base,
                                                                                height: "27px",
                                                                            }),
                                                                            menuPortal: (base) => ({
                                                                                ...base,
                                                                                zIndex: 2000,
                                                                            }),
                                                                            menu: (base) => ({
                                                                                ...base,
                                                                                zIndex: 2000,
                                                                                fontSize: "14px",
                                                                            }),
                                                                        }}
                                                                    />
                                                                </TableCell>

                                                                {/* Destination (dropdown when editing) */}
                                                                <TableCell
                                                                    style={{
                                                                        fontSize: "12px",
                                                                        padding: "6px",
                                                                        borderRight: "1px solid #D6DAC8",
                                                                        width: "300px",
                                                                    }}
                                                                >
                                                                    <Select
                                                                        value={DestinationOptions.find(opt => opt.value === item.DestinationId)}
                                                                        onChange={(selected) => {
                                                                            handleEditChange(index, "DestinationId", selected.value);
                                                                            handleEditChange(index, "DestinationName", selected.label);
                                                                        }}
                                                                        options={DestinationOptions}
                                                                        placeholder="Destination"
                                                                        isSearchable
                                                                        menuPortalTarget={document.body}
                                                                        styles={{
                                                                            control: (base) => ({
                                                                                ...base,
                                                                                minHeight: "27px",
                                                                                height: "27px",
                                                                                fontSize: "14px",
                                                                            }),
                                                                            valueContainer: (base) => ({
                                                                                ...base,
                                                                                height: "27px",
                                                                                padding: "0 6px",
                                                                            }),
                                                                            indicatorsContainer: (base) => ({
                                                                                ...base,
                                                                                height: "27px",
                                                                            }),
                                                                            menuPortal: (base) => ({
                                                                                ...base,
                                                                                zIndex: 2000,
                                                                            }),
                                                                            menu: (base) => ({
                                                                                ...base,
                                                                                zIndex: 2000,
                                                                                fontSize: "14px",
                                                                            }),
                                                                        }}
                                                                    />
                                                                </TableCell>

                                                                {/* Remarks */}
                                                                <TableCell
                                                                    style={{
                                                                        fontSize: "12px",
                                                                        padding: "6px",
                                                                        borderRight: "1px solid #D6DAC8",
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="text"
                                                                        value={item.Remarks}
                                                                        onChange={(e) =>
                                                                            handleEditChange(index, "Remarks", e.target.value)
                                                                        }
                                                                        style={{ width: "100%", fontSize: "14px", padding: "4px" }}
                                                                        maxLength={200}
                                                                    />
                                                                </TableCell>

                                                                {/* Action Buttons */}
                                                                <TableCell
                                                                    style={{
                                                                        fontSize: "12px",
                                                                        padding: "6px",
                                                                        textAlign: "right",
                                                                        borderRight: "1px solid #D6DAC8",
                                                                        width: "180px",
                                                                    }}
                                                                >
                                                                    <button
                                                                        onClick={() => {
                                                                            if (window.confirm("Are you sure you want to delete this record?")) {
                                                                                handleDelete(index);
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            background: "#C53030",
                                                                            color: "white",
                                                                            padding: "4px 8px",
                                                                            borderRadius: "4px",
                                                                            fontSize: "12px",
                                                                            height: "23px",
                                                                        }}
                                                                    >
                                                                        Delete
                                                                    </button>

                                                                    <button
                                                                        onClick={() => handleLoadingExpense(index)}
                                                                        style={{
                                                                            background: expenseItems.some(item => item.ContainerId === index)
                                                                                ? "green"
                                                                                : "#913c0bff",
                                                                            color: "white",
                                                                            padding: "4px 8px",
                                                                            borderRadius: "4px",
                                                                            marginLeft: "5px",
                                                                            fontSize: "12px",
                                                                            height: "23px",
                                                                            width: "110px", // ✅ fixed width (adjust as needed)
                                                                            textAlign: "center",
                                                                        }}
                                                                    >
                                                                        {expenseItems.some(item => item.ContainerId === index)
                                                                            ? "Update Expense"
                                                                            : "Add Expense"}
                                                                    </button>


                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })
                                                )}
                                            </TableBody>


                                        </Table>
                                    </TableContainer>
                                </div>
                            )} {/* end of containerItems.length > 0 */}
                        </div>
                    </div>
                )}
            </div>

            <div
                className="w-full flex flex-col"
                style={{
                    backgroundColor: "whitesmoke",
                    padding: "7px",
                    borderRadius: "10px",
                    marginTop: "10px",
                    boxShadow: "0 6px 10px rgba(0, 0, 0, 0.25), 0 12px 25px rgba(0, 0, 0, 0.2)"
                }}
            >
                {/* Header with expand/collapse button */}
                <div className="flex justify-between items-center">
                    <h6
                        className="uppercase tracking-wide font-serif mt-2"
                        style={{ color: "#103f05ff", fontSize: '13px' }}
                    >
                        Expense List
                    </h6>

                    <button
                        onClick={() => setIsExpandedExpense(!isExpandedExpense)}
                        className="text-sm text-blue-600 flex items-center gap-1"
                        style={{ color: "#103f05ff" }}
                    >
                        {isExpandedExpense ? (
                            <>
                                <ExpandLessIcon size={16} />
                            </>
                        ) : (
                            <>
                                <ExpandMoreIcon size={16} />
                            </>
                        )}
                    </button>
                </div>

                {/* Expandable Section */}
                {isExpandedExpense && (
                    <div className="w-full mt-1">
                        {/* --- your form section --- */}
                        <div className="w-full mb-2 flex items-end gap-2">

                            {/* Expense Head - Larger */}
                            <div className="w-2/5">   {/* <-- make this bigger */}
                                <label
                                    className="block uppercase text-black text-xs mb-1"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px" }}
                                >
                                    Expense Head
                                </label>
                                <Select
                                    value={selectedExpense}
                                    onChange={handleExpenseChange}
                                    options={ExpenseOptions}
                                    placeholder="Expense"
                                    isSearchable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: "30px",   // same as your inputs
                                            height: "30px",
                                            fontSize: "14px",
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            height: "30px",      // keeps text aligned properly
                                            padding: "0 6px",
                                        }),
                                        indicatorsContainer: (base) => ({
                                            ...base,
                                            height: "30px",      // makes dropdown arrow align
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            zIndex: 1301,
                                            fontSize: "14px",
                                        }),
                                    }}
                                />

                            </div>

                            {/* Amount */}
                            <div className="w-1/6">   {/* smaller */}
                                <label className="block uppercase text-black text-xs mb-1"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px" }}>
                                    Amount
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={modalDataExpense.Amount}
                                    onChange={handleModalInputChangeExpense}
                                    placeholder="Amount"
                                    name="Amount"
                                    type="number"
                                    style={{ height: "30px", textAlign: "right", fontSize: '14px' }}
                                />
                            </div>

                            {/* VAT % */}
                            <div className="w-1/6">
                                <label className="block uppercase text-black text-xs mb-1"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px" }}>
                                    VAT %
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={modalDataExpense.VatRate}
                                    onChange={handleModalInputChangeExpense}
                                    placeholder="Vat %"
                                    name="VatRate"
                                    type="number"
                                    style={{ height: "30px", textAlign: "right", fontSize: '14px' }}
                                />
                            </div>

                            {/* VAT Amount */}
                            <div className="w-1/6">
                                <label className="block uppercase text-black text-xs mb-1"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px" }}>
                                    VAT Amount
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={modalDataExpense.VatAmount ? parseFloat(modalDataExpense.VatAmount).toFixed(3) : "0"}
                                    onChange={handleModalInputChangeExpense}
                                    placeholder="Vat Amount"
                                    name="VatAmount"
                                    type="number"
                                    style={{ height: "30px", textAlign: "right", fontSize: '14px' }}
                                    readOnly
                                />
                            </div>

                            {/* Total Amount */}
                            <div className="w-1/6">
                                <label className="block uppercase text-black text-xs mb-1"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px" }}>
                                    Total Amount
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    // value={modalDataExpense.TotalAmount}
                                    value={modalDataExpense.TotalAmount ? parseFloat(modalDataExpense.TotalAmount).toFixed(3) : "0"}
                                    onChange={handleModalInputChangeExpense}
                                    placeholder="Total"
                                    name="TotalAmount"
                                    type="number"
                                    style={{ height: "30px", textAlign: "right", fontSize: '14px' }}
                                    readOnly
                                />
                            </div>

                            {/* Remarks */}
                            <div className="w-2/6">
                                <label
                                    className="block uppercase text-black text-xs mb-1"
                                    style={{ fontFamily: "monospace", fontSize: "12.5px" }}
                                >
                                    Remarks
                                </label>
                                <input
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                    value={modalDataExpense.Remarks}
                                    onChange={handleModalInputChangeExpense}
                                    type="text"
                                    placeholder="Remarks"
                                    name="Remarks"
                                    maxLength={200}
                                    style={{ height: "30px", fontSize: '14px' }}
                                />
                            </div>


                            {/* Add Button */}
                            <div className="w-auto">
                                <button
                                    onClick={handleAddExpense}
                                    className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white font-medium px-2 py-1 rounded-sm shadow-sm transition duration-200 ease-in-out text-sm"
                                    style={{ height: "28px", minWidth: "75px", borderRadius: "5px" }}
                                >
                                    <AddIcon fontSize="inherit" style={{ fontSize: "14px" }} /> Add
                                </button>
                            </div>
                        </div>

                        {/* Table Section */}
                        <div className="w-full pr-4" style={{ marginTop: "-3px" }}>
                            {expenseItems.length > 0 && (
                                <div
                                    className="mb-2 flex"
                                    style={{
                                        maxHeight: "450px",
                                        overflowY: "auto",
                                        marginTop: "3px",
                                    }}
                                >
                                    <TableContainer component={Paper}>
                                        <Table stickyHeader>
                                            <TableHead stickyHeader>
                                                <TableRow>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}
                                                    >
                                                        Sl.No
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}>
                                                        Expense Head
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}>
                                                        Remarks
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}
                                                    >
                                                        Amount (RO)
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}
                                                    >
                                                        VAT %
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}
                                                    >
                                                        VAT Amount (RO)
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                        }}
                                                    >
                                                        Total Amount (RO)
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontSize: "11px",
                                                            backgroundColor: "#435560",
                                                            padding: "1px",
                                                            color: "whitesmoke",
                                                            textTransform: "uppercase",
                                                            fontWeight: "bolder",
                                                            textAlign: "right"
                                                        }}
                                                    >

                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {expenseItems.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={7} align="center" style={{ fontSize: "12px", padding: "10px" }}>
                                                            No Expense added yet.
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    expenseItems.map((item, index) => (
                                                        <TableRow key={index} hover>
                                                            {/* Sl.No (not editable) */}
                                                            <TableCell
                                                                style={{
                                                                    fontSize: "12px",
                                                                    padding: "6px",
                                                                    textAlign: "center",
                                                                    borderRight: "1px solid #D6DAC8",
                                                                    width: "50px",
                                                                }}
                                                            >
                                                                {index + 1}
                                                            </TableCell>
                                                            {/* IncomeExpenseName (dropdown when editing) */}
                                                            <TableCell style={{ fontSize: "12px", padding: "6px", borderRight: "1px solid #D6DAC8", width: "300px", }}>
                                                                <Select
                                                                    value={ExpenseOptions.find(opt => opt.value === item.IncomeExpenseId)}
                                                                    onChange={(selected) => {
                                                                        handleEditExpense(index, "IncomeExpenseId", selected.value);
                                                                        handleEditExpense(index, "IncomeExpenseName", selected.label); // store name also
                                                                    }}
                                                                    options={ExpenseOptions}
                                                                    placeholder="Expense Name"
                                                                    isSearchable
                                                                    menuPortalTarget={document.body} // ✅ render menu outside table container
                                                                    styles={{
                                                                        control: (base) => ({
                                                                            ...base,
                                                                            minHeight: "27px",   // same as your inputs
                                                                            height: "27px",
                                                                            fontSize: "14px",
                                                                        }),
                                                                        valueContainer: (base) => ({
                                                                            ...base,
                                                                            height: "27px",      // keeps text aligned properly
                                                                            padding: "0 6px",
                                                                        }),
                                                                        indicatorsContainer: (base) => ({
                                                                            ...base,
                                                                            height: "27px",      // makes dropdown arrow align
                                                                        }),
                                                                        menu: (provided) => ({
                                                                            ...provided,
                                                                            zIndex: 1301,
                                                                            fontSize: "14px",
                                                                        }),
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            {/* Remarks */}
                                                            <TableCell style={{ fontSize: "12px", padding: "6px", borderRight: "1px solid #D6DAC8", }}>
                                                                <input
                                                                    type="text"
                                                                    value={item.Remarks}
                                                                    onChange={(e) =>
                                                                        handleEditExpense(index, "Remarks", e.target.value)
                                                                    }
                                                                    style={{ width: "100%", fontSize: "14px", padding: "4px" }}
                                                                    maxLength={200}
                                                                />
                                                            </TableCell>
                                                            {/* Amount */}
                                                            <TableCell
                                                                style={{
                                                                    fontSize: "14px",
                                                                    padding: "6px",
                                                                    textAlign: "right",
                                                                    borderRight: "1px solid #D6DAC8",
                                                                    width: "120px",
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                <input
                                                                    type="number"
                                                                    value={item.Amount}
                                                                    onChange={(e) =>
                                                                        handleEditExpense(index, "Amount", e.target.value)
                                                                    }
                                                                    style={{ width: "100%", fontSize: "14px", padding: "4px", textAlign: "right", fontWeight: 'bold' }}
                                                                />
                                                            </TableCell>
                                                            {/* VAT Rate */}
                                                            <TableCell
                                                                style={{
                                                                    fontSize: "14px",
                                                                    padding: "6px",
                                                                    textAlign: "right",
                                                                    borderRight: "1px solid #D6DAC8",
                                                                    width: "80px",
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                <input
                                                                    type="number"
                                                                    value={item.VatRate}
                                                                    onChange={(e) =>
                                                                        handleEditExpense(index, "VatRate", e.target.value)
                                                                    }
                                                                    style={{ width: "100%", fontSize: "14px", padding: "4px", textAlign: "right", fontWeight: 'bold' }}
                                                                />
                                                            </TableCell>
                                                            {/* VAT Amount */}
                                                            <TableCell
                                                                style={{
                                                                    fontSize: "14px",
                                                                    padding: "6px",
                                                                    textAlign: "right",
                                                                    borderRight: "1px solid #D6DAC8",
                                                                    width: "120px",
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                <input
                                                                    type="number"
                                                                    readOnly
                                                                    value={item.VatAmount ? parseFloat(item.VatAmount).toFixed(3) : "0"}
                                                                    onChange={(e) =>
                                                                        handleEditExpense(index, "VatAmount", e.target.value)
                                                                    }
                                                                    style={{ width: "100%", fontSize: "14px", padding: "4px", textAlign: "right", fontWeight: 'bold' }}
                                                                />
                                                            </TableCell>
                                                            {/* Total Amount */}
                                                            <TableCell
                                                                style={{
                                                                    fontSize: "14px",
                                                                    padding: "6px",
                                                                    textAlign: "right",
                                                                    borderRight: "1px solid #D6DAC8",
                                                                    width: "120px",
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                <input
                                                                    type="number"
                                                                    readOnly
                                                                    value={item.TotalAmount ? parseFloat(item.TotalAmount).toFixed(3) : "0"}
                                                                    onChange={(e) =>
                                                                        handleEditExpense(index, "TotalAmount", e.target.value)
                                                                    }
                                                                    style={{ width: "100%", fontSize: "14px", padding: "4px", textAlign: "right", fontWeight: 'bold' }}
                                                                />
                                                            </TableCell>
                                                            {/* Actions */}
                                                            <TableCell
                                                                style={{
                                                                    fontSize: "12px",
                                                                    padding: "6px",
                                                                    textAlign: "right",
                                                                    width: "80px",
                                                                }}
                                                            >
                                                                <button
                                                                    onClick={() => {
                                                                        if (window.confirm("Are you sure you want to delete this record?")) {
                                                                            handleDeleteExpense(index);
                                                                        }
                                                                    }}
                                                                    className="bg-red-600 hover:bg-red-700 text-white font-medium px-2 py-1 rounded-sm shadow-sm transition duration-200 ease-in-out text-sm"
                                                                    style={{
                                                                        minWidth: "60px",
                                                                        color: "white",
                                                                        padding: "4px 8px",
                                                                        borderRadius: "4px",
                                                                        marginRight: "5px",
                                                                        fontSize: "12px",
                                                                        height: "25px",
                                                                    }}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </TableCell>

                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

        </Fragment >
    );
};

export default InvoiceEdit;
